/**
 * Cart Manager - Consolidated cart functionality
 * Combines: cart-min-tally, cart-subscription-selector, cart-item-remove,
 *           add-to-meal-box, meal-box-cta, preset-meal-box
 *
 * Note: Bundle/subscription properties are injected at add-to-cart time via
 * fetch intercept (no post-add matching needed)
 */

(function() {
  'use strict';

  // =============================================================================
  // Shared Utilities
  // =============================================================================
  const CartUtils = {
    // Minimum items - loaded from window.MIN_ITEMS (set by Liquid based on currency)
    // USD: 10, CAD: 14
    get MIN_ITEMS() {
      return window.MIN_ITEMS || 14;
    },

    // Selling plan IDs - loaded from window.AWTOMIC_SELLING_PLANS (set by Liquid)
    // Falls back to empty object if not set
    get SELLING_PLANS() {
      return window.AWTOMIC_SELLING_PLANS || {};
    },

    // Awtomic Bundle configuration - loaded from window.AWTOMIC_BUNDLE_CONFIG (set by Liquid)
    // Falls back to defaults if not set (shouldn't happen in normal operation)
    get BUNDLE_CONFIG() {
      return window.AWTOMIC_BUNDLE_CONFIG || {
        handle: 'pet-meal-box',
        productId: '',
        variantId: '',
        name: 'Pet Meal Box',
        label: 'Select at least ' + (window.MIN_ITEMS || 14) + ' meal packages',
        type: 'dynamicBaB'
      };
    },

    // Generate unique bundle ID
    generateBundleUid: function() {
      return 'bundle_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    },

    // Returns true if a product (looked up by handle) counts toward the
    // primary meal-package minimum. Source of truth is window.mealProductData
    // which is populated by snippets/meal-product-data.liquid for the
    // pet-meals collection. Mirrors boxPacker's CATEGORY_MAP semantics —
    // 'Dog Meal' and 'Cat Meal' are primary meals, everything else
    // (Chef Made Treats, Bundle, Branded Merchandise, Gift Card, etc.) is
    // not. Items not in the pet-meals collection (e.g., one-time Bundle
    // products that ended up in cart) return false — they cannot satisfy
    // the meal-min by themselves.
    //
    // Triage 2026-05-05: Puppy Booster's productType in Shopify is mistyped
    // as 'Dog Meal' but is actually a complement; meal-product-data.liquid
    // overrides its product_type to 'Chef Made Treats' before exposure, so
    // this lookup does the right thing without needing to know about it.
    // A Trial / Discovery Box is a `Bundle` from the recipe-samplers collection.
    // It is NOT in window.mealProductData (that is built from pet-meals), so
    // isPrimaryMeal() is false for it and its 10 meals count ZERO toward the
    // minimum. Policy (T&S team, 2026-08-11): a Trial Box in the cart CLEARS
    // the meal-package minimum. Trial boxes are one-time-purchase only and are
    // never bundled into a subscription.
    isTrialBox: function(handle) {
      if (!handle) return false;
      var handles = window.trialBoxHandles || [];
      return handles.indexOf(handle) !== -1;
    },

    // Variant-id flavour, for the add-to-cart / interceptor paths that only
    // have the variant id. Trial boxes are ONE-TIME PURCHASE ONLY — they must
    // never receive a selling_plan.
    // /cart/add.js accepts a single item, an {items:[...]} batch, or FormData.
    // Returns every variant id in the request so the caller can inspect them.
    variantIdsFromAddBody: function(body) {
      try {
        var data;
        if (body instanceof FormData) {
          data = {};
          body.forEach(function (v, k) { data[k] = v; });
        } else if (typeof body === 'string') {
          data = JSON.parse(body);
        } else if (body && typeof body === 'object') {
          data = body;
        } else {
          return [];
        }
        if (Array.isArray(data.items)) return data.items.map(function (i) { return i.id; }).filter(Boolean);
        return data.id ? [data.id] : [];
      } catch (e) {
        return [];
      }
    },

    isTrialBoxByVariant: function(variantId) {
      if (!variantId) return false;
      var ids = window.trialBoxVariantIds || [];
      return ids.indexOf(String(variantId)) !== -1;
    },

    // OPSAPPS-75: should the Subscribe & Save selector be offered for this cart?
    //
    // A Trial Box contributes NOTHING subscribable — it is one-time only and its
    // meals are not the customer's own box. It does clear the checkout minimum,
    // so a cart of [trial box + 1 meal] can now reach checkout; offering
    // subscription there would create a 1-meal subscription, far below the
    // meal-package minimum (the OPSAPPS-32 failure class). When a Trial Box is
    // present the selector therefore requires a full box of the customer's own
    // meals. Carts WITHOUT a trial box are untouched.
    subscriptionUnavailable: function(cart) {
      if (!cart || !cart.items || !cart.items.length) return false;
      var hasTrial = false;
      var primaryMeals = 0;
      for (var i = 0; i < cart.items.length; i++) {
        var item = cart.items[i];
        if (this.isTrialBox(item.handle)) hasTrial = true;
        if (this.isPrimaryMeal(item.handle)) primaryMeals += item.quantity;
      }
      return hasTrial && primaryMeals < this.MIN_ITEMS;
    },

    // OPSAPPS-75: a Trial Box clears the meal-package minimum ONLY when it is the
    // whole order. If the customer also has meals of their own, those meals must
    // still reach the minimum — meals auto-bundle into a Build-a-Box and
    // auto-subscribe, and the minimum is what stops a sub-minimum BaB reaching
    // checkout. Clearing it unconditionally let [4 auto-bundled meals] + [trial
    // box] check out as a 4-item bundle, breaking the Awtomic portal and needing
    // a manual basket rebuild (OPSAPPS-30/32/33 malformation class).
    trialClearsMinimum: function(cart) {
      if (!cart || !cart.items || !cart.items.length) return false;
      var hasTrial = false;
      var hasBundled = false;
      for (var i = 0; i < cart.items.length; i++) {
        var item = cart.items[i];
        if (this.isTrialBox(item.handle)) hasTrial = true;
        // A line carrying a selling plan or a _bundleUid is part of a Build-a-Box.
        // Order-dependent in practice: trial-box-first leaves meals loose and
        // unbundled, meals-first auto-bundles them. Only the latter is dangerous.
        if (item.selling_plan_allocation) hasBundled = true;
        if (item.properties && item.properties._bundleUid) hasBundled = true;
      }
      return hasTrial && !hasBundled;
    },

    // OPSAPPS-75: MPQ meal plans and Trial Boxes are MUTUALLY EXCLUSIVE. A Trial
    // Box is one-time-only while a Meal Plan is built to drive a subscription, so
    // a cart holding both is a contradiction in offerings — and it is the source
    // of the conflicting subscription/bundle rules this ticket kept unpicking.
    // MPQ-generated lines carry _mealPlanId (see cart grouping).
    cartHasMealPlan: function(cart) {
      return this.mealPlanCount(cart) > 0;
    },

    // Distinct _mealPlanId values — one per plan, however many lines it spans.
    // Mirrors meal_plan_count on the cart page.
    mealPlanCount: function(cart) {
      if (!cart || !cart.items) return 0;
      var ids = {};
      cart.items.forEach(function (i) {
        var id = i.properties && i.properties._mealPlanId;
        if (id) ids[id] = true;
      });
      return Object.keys(ids).length;
    },

    cartHasTrialBox: function(cart) {
      if (!cart || !cart.items) return false;
      for (var i = 0; i < cart.items.length; i++) {
        if (this.isTrialBox(cart.items[i].handle)) return true;
      }
      return false;
    },

    isPrimaryMeal: function(handle) {
      if (!handle) return false;
      var data = (window.mealProductData || {})[handle];
      if (!data) return false;
      var t = data.product_type;
      return t === 'Dog Meal' || t === 'Cat Meal';
    },

    // Variant-id flavour of isPrimaryMeal, for the add-to-cart paths that only
    // have the variant id (not the handle). Reverse-looks-up window.mealProductData
    // (handle-keyed, exposes variant_id per product). Returns true ONLY for primary
    // meals (Dog Meal / Cat Meal) — add-ons/complements (Chef Made Treats, e.g.
    // Puppy Booster via the meal-product-data.liquid override), merch, gift cards,
    // and anything not in the pet-meals collection return false. This is the gate
    // that decides whether bundle properties (_bundleUid etc.) get stamped: only
    // primary meals are bundle components; everything else is a standalone line.
    isPrimaryMealByVariant: function(variantId) {
      if (!variantId) return false;
      var data = window.mealProductData || {};
      var vid = String(variantId);
      for (var handle in data) {
        if (String(data[handle].variant_id) === vid) {
          var t = data[handle].product_type;
          return t === 'Dog Meal' || t === 'Cat Meal';
        }
      }
      return false;
    },

    // Decide whether to stamp bundle props (_bundleUid etc.) on a subscription
    // cart line: ONLY primary meals are bundle components. Defensive fallback —
    // if window.mealProductData hasn't loaded at all (it's rendered globally just
    // before cart-manager in theme.liquid, so this shouldn't happen), preserve
    // legacy behavior (bundle) so we never silently break meal-box bundling. Worst
    // case in that degraded state = today's behavior (add-ons bundle), never worse.
    shouldBundleVariant: function(variantId) {
      var data = window.mealProductData;
      if (!data || Object.keys(data).length === 0) return true;
      return this.isPrimaryMealByVariant(variantId);
    },

    // Fetch cart data
    fetchCart: function() {
      return fetch('/cart.js').then(res => res.json());
    },

    // Format money (cents to dollars)
    formatMoney: function(cents) {
      return '$' + (cents / 100).toFixed(2);
    },

    // Announce message to screen readers via aria-live region
    announceToScreenReader: function(message) {
      var announcer = document.getElementById('cart-status-announcer');
      if (announcer) {
        // Clear then set to trigger announcement
        announcer.textContent = '';
        setTimeout(function() {
          announcer.textContent = message;
        }, 100);
      }
    },

    // Clear entire cart using dedicated clear endpoint
    // Note: /cart/clear.js is more reliable than /cart/update.js with Awtomic bundles
    clearCart: function(callback) {
      return fetch('/cart/clear.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      })
        .then(res => res.json())
        .then(cart => {
          // Verify cart is actually empty
          if (cart.item_count !== 0) {
            console.warn('[CartUtils.clearCart] Cart not fully cleared, retrying...');
            // Retry once if items remain
            return fetch('/cart/clear.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' }
            }).then(r => r.json());
          }
          return cart;
        })
        .then(cart => {
          console.log('[CartUtils.clearCart] Cart cleared, item_count:', cart.item_count);

          // Track cart cleared event
          if (window.AnalyticsTracker) {
            window.AnalyticsTracker.push({
              event: 'cart_cleared'
            });
          }

          if (callback) {
            callback();
          }
        });
    },

    // Show loading overlay on cart drawer
    showCartLoading: function() {
      const cartDrawer = document.querySelector('#CartDrawer .drawer__scrollable');
      if (!cartDrawer) return;

      let overlay = document.querySelector('.cart-updating-overlay');
      if (!overlay) {
        overlay = document.createElement('div');
        overlay.className = 'cart-updating-overlay';
        overlay.innerHTML = '<div class="cart-updating-message">Updating...</div>';
        cartDrawer.appendChild(overlay);

        const style = document.createElement('style');
        style.textContent = `
          .cart-updating-overlay {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(255, 255, 255, 0.9);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 100;
          }
          .cart-updating-message {
            font-size: 16px;
            font-weight: 500;
            color: #333;
          }
        `;
        document.head.appendChild(style);
      }
      overlay.style.display = 'flex';
    },

    // Hide loading overlay on cart drawer
    hideCartLoading: function() {
      const overlay = document.querySelector('.cart-updating-overlay');
      if (overlay) {
        overlay.style.display = 'none';
      }
    },

    // Get the current cart's selling plan (frequency)
    // Returns null if cart has items but no subscription (one-time cart)
    // Only defaults to 4-week for empty carts
    getCartSellingPlan: function(cart) {
      if (cart && cart.items && cart.items.length > 0) {
        // Find an item with a selling plan allocation
        const itemWithSubscription = cart.items.find(item => item.selling_plan_allocation);
        if (itemWithSubscription && itemWithSubscription.selling_plan_allocation) {
          return itemWithSubscription.selling_plan_allocation.selling_plan.id;
        }
        // Cart has items but no subscription - this is a one-time cart
        return null;
      }
      // Empty cart - default to 4-week subscription
      return this.SELLING_PLANS['4'];
    },

    // Get the active selling plan from cached cart state (synchronous)
    // Returns the selling plan ID if cart has subscription, null otherwise
    getActiveSellingPlan: function() {
      const { sellingPlan } = CartStateCache.get();
      return sellingPlan;
    },

    // Rebuild cart (trigger theme's cart rebuild)
    rebuildCart: function() {
      if (window.theme && window.theme.cart && window.theme.cart.buildCart) {
        window.theme.cart.buildCart();
      }
      document.dispatchEvent(new CustomEvent('cart:build'));
      document.dispatchEvent(new CustomEvent('cart:updated'));
    },

    // Build bundle properties for an item
    buildBundleProperties: function(variantId, bundleUid) {
      return {
        _bundleUid: bundleUid,
        _bundleHandle: this.BUNDLE_CONFIG.handle,
        _bundleType: this.BUNDLE_CONFIG.type,
        _bundleLabel: this.BUNDLE_CONFIG.label,
        _bundleProductId: this.BUNDLE_CONFIG.productId,
        _bundleName: this.BUNDLE_CONFIG.name,
        _variantId: 'gid://shopify/ProductVariant/' + variantId
      };
    },

    // Get existing bundleUid from cart or generate new one
    // Only reuses bundleUid from BYO items (no _mealPlanId) - meal plan bundles are separate
    getBundleUid: function(cart) {
      if (cart && cart.items) {
        // Find existing BYO bundle item (has _bundleUid but NO _mealPlanId)
        const existingByoItem = cart.items.find(item =>
          item.properties &&
          item.properties._bundleUid &&
          !item.properties._mealPlanId
        );
        if (existingByoItem) {
          return existingByoItem.properties._bundleUid;
        }
      }
      return this.generateBundleUid();
    },

    // Build item payload for add-to-cart (handles subscription vs one-time)
    buildAddToCartPayload: function(variantId, cart) {
      const sellingPlan = this.getCartSellingPlan(cart);
      const itemPayload = {
        id: variantId,
        quantity: 1
      };

      // Trial boxes are one-time-purchase only — never attach a selling plan,
      // even when the rest of the cart is a subscription.
      if (sellingPlan && !this.isTrialBoxByVariant(variantId)) {
        // Subscription cart - always attach the selling plan so the line recurs.
        itemPayload.selling_plan = parseInt(sellingPlan, 10);
        // Bundle properties (_bundleUid etc.) ONLY for primary meals. Add-ons /
        // complements (Puppy Booster etc.) recur as their own standalone line —
        // they are NOT components of the Pet Meal Box bundle.
        if (this.shouldBundleVariant(variantId)) {
          const bundleUid = this.getBundleUid(cart);
          itemPayload.properties = {
            ...this.buildBundleProperties(variantId, bundleUid),
            _bundleBoxSize: (cart && cart.items ? cart.items.length : 0) + 1
          };
        }
      }
      // else: One-time cart - no bundle properties, no selling plan

      return itemPayload;
    }
  };

  // =============================================================================
  // Cart State Cache
  // Initialized by Liquid (window.CART_STATE), updated after cart operations
  // =============================================================================
  const CartStateCache = {
    // Update cache from cart response
    update: function(cart) {
      if (!window.CART_STATE) {
        window.CART_STATE = {};
      }

      window.CART_STATE.itemCount = cart.item_count || 0;

      if (cart.items && cart.items.length > 0) {
        const itemWithSub = cart.items.find(i => i.selling_plan_allocation);
        if (itemWithSub) {
          window.CART_STATE.sellingPlan = String(itemWithSub.selling_plan_allocation.selling_plan.id);
          window.CART_STATE.bundleUid = itemWithSub.properties?._bundleUid || null;
        } else {
          // Cart has items but no subscription
          delete window.CART_STATE.sellingPlan;
          delete window.CART_STATE.bundleUid;
        }
      } else {
        // Empty cart
        delete window.CART_STATE.sellingPlan;
        delete window.CART_STATE.bundleUid;
      }
    },

    // Get current state (from cache or defaults)
    // Only first item to empty cart gets defaults; subsequent items match existing state
    get: function() {
      const state = window.CART_STATE || {};

      if (state.itemCount > 0) {
        // Cart has items - match existing state (subscription or one-time)
        if (state.sellingPlan) {
          return {
            sellingPlan: state.sellingPlan,
            bundleUid: state.bundleUid || CartUtils.generateBundleUid()
          };
        } else {
          // One-time cart - no subscription
          return {
            sellingPlan: null,
            bundleUid: null
          };
        }
      }

      // Empty cart - use defaults (4-week subscription)
      return {
        sellingPlan: CartUtils.SELLING_PLANS['4'],
        bundleUid: CartUtils.generateBundleUid()
      };
    }
  };

  // =============================================================================
  // Fetch Intercept
  // - /cart/add.js: Inject bundle + subscription properties (uses cached state)
  // - /cart.js: Update cache from response
  // Skips if request already has selling_plan (e.g., from applySubscription)
  // =============================================================================
  const originalFetch = window.fetch;
  window.fetch = async function(url, options) {
    const urlStr = typeof url === 'string' ? url : url?.url || '';

    // Intercept /cart.js responses to update cache
    if (urlStr.includes('/cart.js') && !urlStr.includes('/cart/')) {
      const response = await originalFetch.call(this, url, options);
      // Clone response so we can read it and still return it
      const cloned = response.clone();
      try {
        const cart = await cloned.json();
        CartStateCache.update(cart);
      } catch (e) {
        // Ignore parse errors
      }
      return response;
    }

    // Update cache after cart-modifying endpoints (clear, update, change)
    // These return cart data we can use to keep cache in sync
    if (urlStr.includes('/cart/clear.js') || urlStr.includes('/cart/update.js') || urlStr.includes('/cart/change.js')) {
      const response = await originalFetch.call(this, url, options);
      const cloned = response.clone();
      try {
        const cart = await cloned.json();
        CartStateCache.update(cart);
      } catch (e) {
        // Ignore parse errors
      }
      return response;
    }

    // Intercept /cart/add.js POST requests
    // - Inject bundle + subscription properties if needed
    // - Update cache after response (add returns only added items, so fetch full cart)
    if (urlStr.includes('/cart/add.js') && options?.method === 'POST') {
      let shouldUpdateCache = true;

      // OPSAPPS-75: adding a Trial Box to a cart that holds an MPQ meal plan.
      // Mutually exclusive — ask, then either drop the meal plan or abandon the add.
      try {
        const addedIds = CartUtils.variantIdsFromAddBody(options.body);
        if (addedIds.some(id => CartUtils.isTrialBoxByVariant(id))) {
          const currentCart = await originalFetch.call(this, '/cart.js').then(r => r.json());
          if (CartUtils.cartHasMealPlan(currentCart)) {
            // Pluralise on the number of DISTINCT plans, not line items — a single
            // plan spans several lines, so counting items would read "Plans" for
            // one plan.
            const planCount = CartUtils.mealPlanCount(currentCart);
            const plan = planCount === 1 ? 'Meal Plan' : 'Meal Plans';
            const accepted = await Toast.confirm(
              planCount === 1
                ? 'You have a prepared meal plan in your cart'
                : 'You have prepared meal plans in your cart',
              {
                confirmText: 'Swap ' + plan + ' for Trial Box',
                cancelText: 'Keep ' + plan,
                type: 'warning'
              }
            );
            if (!accepted) {
              return new Response(
                JSON.stringify({ status: 422, message: 'Cancelled', description: 'Trial Box was not added — your meal plan is unchanged.' }),
                { status: 422, headers: { 'Content-Type': 'application/json' } }
              );
            }
            const updates = {};
            currentCart.items.forEach(function (i) {
              if (i.properties && i.properties._mealPlanId) updates[i.key] = 0;
            });
            if (Object.keys(updates).length) {
              await originalFetch.call(this, '/cart/update.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ updates: updates })
              });
              document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
            }
          }
        }
      } catch (guardErr) {
        // Never block an add on the guard itself.
        console.error('[TrialBox/MealPlan guard]', guardErr);
      }

      try {
        // Parse the request body first to check if it already has selling_plan
        let body = options.body;
        let isFormData = false;
        let data;

        if (body instanceof FormData) {
          isFormData = true;
          data = {};
          body.forEach((value, key) => { data[key] = value; });
        } else if (typeof body === 'string') {
          // Could be JSON or URL-encoded
          try {
            data = JSON.parse(body);
          } catch {
            // URL-encoded form data
            data = Object.fromEntries(new URLSearchParams(body));
            isFormData = true;
          }
        } else if (typeof body === 'object') {
          data = body;
        }

        // Property-injection rules:
        // - SINGLE-item add (data.id): skip injection if the request already
        //   carries selling_plan or _bypassSubscriptionIntercept — caller has
        //   already decided what to do with this line (applySubscription /
        //   explicit one-time path).
        // - BULK add (data.items[]): decide PER-ITEM. Pre-2026-05-05 this
        //   path was all-or-nothing: if ANY item carried selling_plan
        //   (e.g. one item pre-tagged by volt-meal-plan widget) the .some()
        //   check skipped injection for EVERY item. Result: that one item
        //   kept its bundle attrs but the other 4 in the batch landed in
        //   cart with no _bundleUid → orphan-bundle bug (#75658 Susan Kerr,
        //   May 2). Now each item is evaluated independently — pre-tagged
        //   items pass through untouched, untagged items get the current
        //   subscription's selling_plan + bundle properties.
        const singleAlreadyTagged =
          (data && 'selling_plan' in data) ||
          (data && data._bypassSubscriptionIntercept) ||
          // Trial boxes are one-time-purchase only — never inject a selling plan.
          (data && CartUtils.isTrialBoxByVariant(data.id));

        // Strip top-level bypass flag before send (not a valid cart prop)
        if (data?._bypassSubscriptionIntercept) {
          delete data._bypassSubscriptionIntercept;
          options.body = JSON.stringify(data);
        }

        // Use cached cart state (no fetch needed)
        const { sellingPlan, bundleUid } = CartStateCache.get();

        if (sellingPlan !== null) {
          if (data && data.id) {
            // Single-item path — preserve previous gate
            if (!singleAlreadyTagged) {
              const variantId = data.id;
              // Always attach the selling plan so the line recurs with the sub.
              data.selling_plan = sellingPlan;
              // Bundle props (_bundleUid etc.) ONLY for primary meals. Add-ons /
              // complements (e.g. Puppy Booster) recur as their own standalone
              // line — never as a Pet Meal Box bundle component.
              if (CartUtils.shouldBundleVariant(variantId)) {
                const bundleProps = CartUtils.buildBundleProperties(variantId, bundleUid);
                data.properties = { ...(data.properties || {}), ...bundleProps };
              }

              if (isFormData) {
                const params = new URLSearchParams();
                Object.keys(data).forEach(key => {
                  if (key === 'properties' && typeof data[key] === 'object') {
                    Object.keys(data[key]).forEach(propKey => {
                      params.append(`properties[${propKey}]`, data[key][propKey]);
                    });
                  } else {
                    params.append(key, data[key]);
                  }
                });
                options.body = params.toString();
              } else {
                options.body = JSON.stringify(data);
              }
            }
          } else if (data && data.items && Array.isArray(data.items)) {
            // Bulk-add path — per-item decision
            let mutated = false;
            data.items = data.items.map(item => {
              const itemAlreadyTagged =
                ('selling_plan' in item) ||
                item._bypassSubscriptionIntercept ||
                // Trial boxes are one-time-purchase only.
                CartUtils.isTrialBoxByVariant(item.id);
              // Strip per-item bypass flag (not a valid cart prop)
              const cleanItem = { ...item };
              if (cleanItem._bypassSubscriptionIntercept) {
                delete cleanItem._bypassSubscriptionIntercept;
              }
              if (itemAlreadyTagged) return cleanItem;

              mutated = true;
              // Bundle props (_bundleUid etc.) ONLY for primary meals. Add-ons /
              // complements recur as their own standalone line (selling_plan only).
              if (CartUtils.shouldBundleVariant(item.id)) {
                const bundleProps = CartUtils.buildBundleProperties(item.id, bundleUid);
                return {
                  ...cleanItem,
                  properties: { ...(cleanItem.properties || {}), ...bundleProps },
                  selling_plan: sellingPlan
                };
              }
              return { ...cleanItem, selling_plan: sellingPlan };
            });
            if (mutated) {
              options.body = JSON.stringify(data);
            }
          }
        }
      } catch (error) {
        console.error('[Cart Intercept] Error preparing add request:', error);
        // Proceed with original request on error
      }

      // Execute the request
      const response = await originalFetch.call(this, url, options);

      // Update cache after add completes
      // /cart/add.js returns only added items, so fetch full cart to update cache
      if (shouldUpdateCache) {
        try {
          const cartResponse = await originalFetch.call(this, '/cart.js');
          const cart = await cartResponse.json();
          CartStateCache.update(cart);
        } catch (e) {
          // Ignore errors - cache will be stale but will correct on next cart fetch
        }
      }

      return response;
    }

    return originalFetch.call(this, url, options);
  };

  // =============================================================================
  // Weight Tally Module
  // =============================================================================
  const MealTally = {
    _updateTimeout: null,

    updateMealTally: function(cartData) {
      // Debounce to prevent rapid toggling/flashing
      if (this._updateTimeout) {
        clearTimeout(this._updateTimeout);
      }
      // If cart data provided, use it immediately (no debounce needed)
      if (cartData && cartData.items) {
        this._doUpdateItemTally(cartData);
      } else {
        this._updateTimeout = setTimeout(() => this._doUpdateItemTally(), 50);
      }
    },

    _doUpdateItemTally: function(cartData) {
      const processCart = (cart) => {
          // Count ONLY items that count as primary meals (Dog Meal / Cat Meal
          // per window.mealProductData). Pre-2026-05-05 this summed every
          // line's quantity, which let a 14×Puppy-Booster cart (or any
          // complement-only cart) clear the minimum and reach checkout —
          // boxPacker would then reject the box config server-side. Counting
          // primary meals here matches boxPacker's CATEGORY_MAP semantics
          // and surfaces the existing min-meals warning to customers before
          // payment.
          let totalItems = 0;
          cart.items.forEach(item => {
            if (CartUtils.isPrimaryMeal(item.handle)) {
              totalItems += item.quantity;
            }
          });

          // A Trial Box clears the minimum outright (OPSAPPS-75): it already
          // contains 10 meals, but counts 0 here because it is a Bundle.
          const hasTrialBox = CartUtils.trialClearsMinimum(cart);
          const meetsMinItems = hasTrialBox || totalItems >= CartUtils.MIN_ITEMS;

          // CheckoutMinGuard runs synchronously inside a submit handler and cannot
          // await a cart fetch, so publish the verdict here for it to read.
          const cartForm = document.getElementById('CartPageForm');
          if (cartForm) cartForm.dataset.trialClearsMin = hasTrialBox ? 'true' : 'false';

          // Update item tally display
          const currentItemEls = document.querySelectorAll('.cart-min-tally__current');
          currentItemEls.forEach(function(currentItemEl) {
            currentItemEl.textContent = totalItems;

            // Check context for color
            const isInOverlay = currentItemEl.closest('.cart__min-progress-overlay');
            const isInDrawer = currentItemEl.closest('.drawer--cart');
            const isInCard = currentItemEl.closest('.cart__min-progress-card');
            const isInAnnouncement = currentItemEl.closest('.announcement--cart-progress');

            if (meetsMinItems) {
              currentItemEl.style.color = '#0a8a8f'; // WCAG AA compliant - 4.52:1 contrast ratio
            } else if (isInOverlay || isInCard || isInDrawer || isInAnnouncement) {
              // White for drawer, overlays, and announcement
              currentItemEl.style.color = 'white';
            } else {
              currentItemEl.style.color = '#767676';
            }
          });

          // Update separator and min text colors based on threshold
          const separatorEls = document.querySelectorAll('.cart-min-tally__separator');
          const minTextEls = document.querySelectorAll('.cart-min-tally__min');

          separatorEls.forEach(function(el) {
            const isInProgressText = el.closest('.cart__min-progress-text');
            const isInDrawer = el.closest('.drawer--cart');
            if (isInProgressText) {
              // Drawer keeps white for legibility over progress bar overlay
              if (isInDrawer) {
                el.style.color = 'rgba(255, 255, 255, 1)';
              } else {
                el.style.color = meetsMinItems ? 'rgba(38, 202, 211, 0.7)' : 'rgba(255, 255, 255, 1)';
              }
            }
          });

          minTextEls.forEach(function(el) {
            const isInProgressText = el.closest('.cart__min-progress-text');
            const isInDrawer = el.closest('.drawer--cart');
            if (isInProgressText) {
              // Drawer keeps white for legibility over progress bar overlay
              if (isInDrawer) {
                el.style.color = 'rgba(255, 255, 255, 1)';
              } else {
                el.style.color = meetsMinItems ? 'rgba(38, 202, 211, 0.7)' : 'rgba(255, 255, 255, 1)';
              }
            }
          });

          // Update progress bars (drawer, cart page, and announcement bar)
          // 14 segments for 14 items (1 segment per item)
          const filledSegments = Math.min(totalItems, CartUtils.MIN_ITEMS);
          const progressBars = document.querySelectorAll('[data-min-progress], [data-min-progress-page], [data-min-progress-announcement]');
          progressBars.forEach(function(progressBar) {
            const segments = progressBar.querySelectorAll('.cart__min-segment');
            segments.forEach((seg, i) => {
              if (i < filledSegments) {
                seg.classList.add('cart__min-segment--filled');
              } else {
                seg.classList.remove('cart__min-segment--filled');
              }
            });
            // Add full class when all segments filled
            if (filledSegments >= CartUtils.MIN_ITEMS) {
              progressBar.classList.add('cart__min-progress--full');
            } else {
              progressBar.classList.remove('cart__min-progress--full');
            }
          });

          // Update progress card/overlay full state
          const progressContainers = document.querySelectorAll('.cart__min-progress-card, [data-min-progress-overlay]');
          progressContainers.forEach(function(container) {
            if (filledSegments >= CartUtils.MIN_ITEMS) {
              container.classList.add('cart__min-progress-card--full', 'cart__min-progress-overlay--full');
            } else {
              container.classList.remove('cart__min-progress-card--full', 'cart__min-progress-overlay--full');
            }
          });

          // OPSAPPS-75: hide the meal-package tally outright while a Trial Box is
          // in the cart. The box holds 10 meals but counts 0 here, so the tally
          // would read "0 / 14" next to an enabled checkout button. Liquid does
          // the same on first render; this mirrors it as the cart mutates.
          // OPSAPPS-75: a cart holding nothing but Trial Boxes has nothing that
          // can recur — hide the Subscribe & Save selector. A MIXED cart keeps it
          // (the customer's own meals can subscribe; the trial box stays
          // one-time). Liquid does this on first render; mirrored here because
          // the cart mutates without a reload. The data flag makes the hide
          // reversible without clobbering other display logic (e.g. the cart
          // page hides the selector when meal_plan_count > 1).
          const hideSubscription = CartUtils.subscriptionUnavailable(cart);
          document.querySelectorAll('.cart__subscription-selector').forEach(function (el) {
            if (hideSubscription) {
              if (el.style.display !== 'none') el.dataset.trialHidden = '1';
              el.style.display = 'none';
            } else if (el.dataset.trialHidden) {
              el.style.display = '';
              delete el.dataset.trialHidden;
            }
          });

          const tallySurfaces = document.querySelectorAll(
            '.drawer__header-progress-link, .announcement--cart-progress'
          );
          tallySurfaces.forEach(function(el) {
            el.style.display = hasTrialBox ? 'none' : '';
          });
          if (hasTrialBox) {
            document.body.classList.remove('cart-progress-sticky');
          }

          // Toggle sticky announcement bar on /cart page based on threshold
          const cartProgressAnnouncement = document.querySelector('.announcement--cart-progress');
          if (cartProgressAnnouncement) {
            if (meetsMinItems) {
              cartProgressAnnouncement.classList.remove('announcement--sticky');
              document.body.classList.remove('cart-progress-sticky');
            } else {
              cartProgressAnnouncement.classList.add('announcement--sticky');
              document.body.classList.add('cart-progress-sticky');
            }
          }

          // Update checkout button state (only on /cart page, not drawer)
          // Drawer always allows access to /cart; actual checkout is blocked on /cart page
          const checkoutWrapper = document.querySelector('.cart__checkout:not(.cart__checkout--drawer)');
          if (checkoutWrapper) {
            const checkoutBtn = checkoutWrapper.querySelector('button[type="submit"]');
            if (meetsMinItems) {
              checkoutWrapper.style.display = '';
              if (checkoutBtn) checkoutBtn.removeAttribute('disabled');
            } else {
              checkoutWrapper.style.display = 'none';
              if (checkoutBtn) checkoutBtn.setAttribute('disabled', 'disabled');
            }
          }

          // Subscription selector always visible when cart has items (no item-based hiding)
          // Visibility is controlled by Liquid template based on cart.item_count

          // Hide/show Auto-fill buttons based on item threshold
          // Always update (not just on state change) because cart rebuild re-renders with display:none
          const autofillBtns = document.querySelectorAll('[data-autofill-btn], [data-autofill-byo], #autoFillBtn');
          autofillBtns.forEach(function(btn) {
            if (meetsMinItems) {
              btn.style.display = 'none';
            } else {
              btn.style.display = '';
            }
          });

          // Update cart page warning message
          const itemsNeeded = CartUtils.MIN_ITEMS - totalItems;
          let warningEl = document.querySelector('.cart__min-meal-warning:not(.cart__min-meal-warning--drawer)');
          if (!meetsMinItems) {
            if (!warningEl) {
              // Create warning element if it doesn't exist
              warningEl = document.createElement('p');
              warningEl.className = 'cart__min-meal-warning';

              const checkoutWrapper = document.querySelector('.cart__checkout-wrapper');
              const checkoutBtn = checkoutWrapper ? checkoutWrapper.querySelector('.cart__checkout') : null;
              if (checkoutBtn) {
                checkoutBtn.insertAdjacentElement('afterend', warningEl);
              }
            }
            if (warningEl) {
              warningEl.textContent = `Add ${itemsNeeded} more to reach ${CartUtils.MIN_ITEMS} meal package minimum`;
              warningEl.style.display = 'block';
            }
          } else {
            if (warningEl) {
              warningEl.style.display = 'none';
            }
          }

          // Dispatch event to trigger drawer layout adjustment
          document.dispatchEvent(new CustomEvent('cart:itemsUpdated'));
      };

      // Use provided cart data or fetch fresh
      if (cartData && cartData.items) {
        processCart(cartData);
      } else {
        CartUtils.fetchCart()
          .then(processCart)
          .catch(error => console.error('Error updating item tally:', error));
      }
    },

    init: function() {
      // Update on page load
      if (document.querySelector('.cart-min-tally__current')) {
        this.updateMealTally();
      }

      // Listen for quantity changes
      document.addEventListener('change', (e) => {
        if (e.target.matches('.js-qty__num')) {
          setTimeout(() => {
            this.updateMealTally();
            // Update price display to recalculate virtual subscription discount
            if (SubscriptionSelector && SubscriptionSelector.updatePriceDisplay) {
              SubscriptionSelector.updatePriceDisplay(false);
            }
          }, 800);
        }
      });

      // Listen for +/- button clicks
      document.addEventListener('click', (e) => {
        if (e.target.closest('.js-qty__adjust')) {
          setTimeout(() => {
            this.updateMealTally();
            // Update price display to recalculate virtual subscription discount
            if (SubscriptionSelector && SubscriptionSelector.updatePriceDisplay) {
              SubscriptionSelector.updatePriceDisplay(false);
            }
          }, 800);
        }
      });

      // Listen for cart update events - use cart data from event if available
      document.addEventListener('cart:updated', (e) => {
        const cart = e.detail && e.detail.cart;
        setTimeout(() => {
          this.updateMealTally(cart);
          // Update price display to recalculate virtual subscription discount
          if (SubscriptionSelector && SubscriptionSelector.updatePriceDisplay) {
            SubscriptionSelector.updatePriceDisplay(false);
          }
        }, 100);
      });

      // Also listen for cart:built (after DOM rebuild) - use cart data from event
      document.addEventListener('cart:built', (e) => {
        const cart = e.detail && e.detail.cart;
        // If cart data provided, update immediately; otherwise short delay
        if (cart) {
          this.updateMealTally(cart);
        } else {
          setTimeout(() => this.updateMealTally(), 50);
        }
      });

      // Listen for product added - update tally immediately
      document.addEventListener('ajaxProduct:added', (e) => {
        const cart = e.detail && e.detail.cart;
        this.updateMealTally(cart);
      });
    }
  };

  // =============================================================================
  // Subscription Selector Module
  // =============================================================================
  const SubscriptionSelector = {
    conversionInProgress: false, // Flag to prevent concurrent conversions
    _checkoutBtnOriginalContent: '', // Store original button content during conversion

    // Disable/enable radio buttons during conversion
    setRadioButtonsEnabled: function(enabled) {
      const radios = document.querySelectorAll('input[name="cart-plan-selector"], input[name="cart-plan-selector-drawer"]');
      radios.forEach(radio => {
        radio.disabled = !enabled;
      });
    },

    // Show loading state on checkout button (works with both button and anchor elements)
    setCheckoutLoading: function(loading) {
      const checkoutBtn = document.querySelector('.cart__checkout--drawer');
      if (!checkoutBtn) return;

      if (loading) {
        this._checkoutBtnOriginalContent = checkoutBtn.innerHTML;
        this._checkoutBtnOriginalHref = checkoutBtn.getAttribute('href');
        // For buttons use disabled, for anchors use pointer-events and aria
        if (checkoutBtn.tagName === 'BUTTON') {
          checkoutBtn.disabled = true;
        } else {
          checkoutBtn.style.pointerEvents = 'none';
          checkoutBtn.setAttribute('aria-disabled', 'true');
          checkoutBtn.removeAttribute('href');
        }
        const textSpan = checkoutBtn.querySelector('[data-meal-box-text]');
        if (textSpan) {
          textSpan.textContent = 'Updating...';
        } else {
          checkoutBtn.textContent = 'Updating...';
        }
      } else if (this._checkoutBtnOriginalContent) {
        if (checkoutBtn.tagName === 'BUTTON') {
          checkoutBtn.disabled = false;
        } else {
          checkoutBtn.style.pointerEvents = '';
          checkoutBtn.removeAttribute('aria-disabled');
          if (this._checkoutBtnOriginalHref) {
            checkoutBtn.setAttribute('href', this._checkoutBtnOriginalHref);
          }
        }
        checkoutBtn.innerHTML = this._checkoutBtnOriginalContent;
        this._checkoutBtnOriginalContent = '';
        this._checkoutBtnOriginalHref = '';
      }
    },

    // Reset conversion state (called on success or error)
    resetConversionState: function() {
      this.conversionInProgress = false;
      this.setRadioButtonsEnabled(true);
      this.setCheckoutLoading(false);
    },

    // Apply selling plan to all cart items using atomic clear + re-add pattern
    // This prevents race conditions that occur with parallel /cart/change.js calls
    applySubscription: function(frequency, callback) {
      // Prevent concurrent operations
      if (this.conversionInProgress) {
        console.warn('[SubscriptionSelector] Conversion already in progress, ignoring request');
        return Promise.reject('Conversion already in progress');
      }

      const sellingPlan = CartUtils.SELLING_PLANS[frequency];
      if (!sellingPlan) {
        console.error('Invalid frequency:', frequency);
        return Promise.reject('Invalid frequency');
      }

      this.conversionInProgress = true;
      this.setRadioButtonsEnabled(false);
      this.setCheckoutLoading(true);

      return CartUtils.fetchCart()
        .then(cart => {
          if (cart.items.length === 0) {
            return cart; // Nothing to do
          }

          // Get existing bundleUid or generate new one
          const bundleUid = CartUtils.getBundleUid(cart);

          // Build items array with selling_plan and bundle properties, consolidating by variant_id
          const variantMap = new Map();
          cart.items.forEach(item => {
            const variantId = item.variant_id;
            if (variantMap.has(variantId)) {
              // Consolidate: add quantity to existing entry
              variantMap.get(variantId).quantity += item.quantity;
            } else {
              // Trial boxes stay one-time even when the cart converts to a
              // subscription — they are not offered on subscription at all.
              const entry = {
                id: variantId,
                quantity: item.quantity
              };
              if (!CartUtils.isTrialBoxByVariant(variantId)) {
                entry.selling_plan = parseInt(sellingPlan, 10);
              }
              // Bundle props (_bundleUid etc.) ONLY for primary meals. Add-ons /
              // complements in the cart convert to standalone recurring lines,
              // not Pet Meal Box bundle components.
              if (CartUtils.shouldBundleVariant(variantId)) {
                entry.properties = {
                  ...CartUtils.buildBundleProperties(variantId, bundleUid),
                  _bundleBoxSize: cart.items.length
                };
              }
              variantMap.set(variantId, entry);
            }
          });

          // Reverse to preserve original cart order (Shopify /cart/add.js prepends items)
          const itemsToAdd = Array.from(variantMap.values()).reverse();

          // Step 1: Clear cart completely
          return fetch('/cart/clear.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
          })
          .then(res => res.json())
          .then(clearedCart => {
            // Verify cart is empty
            if (clearedCart.item_count !== 0) {
              throw new Error('Failed to clear cart');
            }

            // Step 2: Re-add all items atomically with selling_plan
            return fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ items: itemsToAdd })
            });
          })
          .then(res => res.json());
        })
        .then(() => {
          this.resetConversionState();
          CartUtils.announceToScreenReader('Subscription applied. Delivery every ' + frequency + ' weeks.');
          if (callback) callback();
        })
        .catch(error => {
          console.error('Error applying subscription:', error);
          this.resetConversionState();
          Toast.error('There was an error applying your subscription. Please try again.');
          CartUtils.rebuildCart();
        });
    },

    // Remove selling plan from cart items (convert to one-time purchase)
    // Uses atomic clear + re-add pattern to prevent race conditions
    removeSubscription: function(callback) {
      // Prevent concurrent operations
      if (this.conversionInProgress) {
        console.warn('[SubscriptionSelector] Conversion already in progress, ignoring request');
        return Promise.reject('Conversion already in progress');
      }

      this.conversionInProgress = true;
      this.setRadioButtonsEnabled(false);
      this.setCheckoutLoading(true);

      return CartUtils.fetchCart()
        .then(cart => {
          if (cart.items.length === 0) {
            return cart; // Nothing to do
          }

          // Build items array WITHOUT selling_plan, consolidating by variant_id
          const variantMap = new Map();
          cart.items.forEach(item => {
            const variantId = item.variant_id;
            if (variantMap.has(variantId)) {
              // Consolidate: add quantity to existing entry
              variantMap.get(variantId).quantity += item.quantity;
            } else {
              // Create item WITHOUT selling_plan (one-time purchase)
              // Also strip bundle properties and meal plan properties
              // (meal plan is frequency-dependent, so invalid for one-time)
              const cleanProperties = {};
              if (item.properties) {
                Object.keys(item.properties).forEach(key => {
                  // Keep properties that aren't bundle or meal plan related
                  // Remove: _bundle*, _variant*, _mealPlan*, _petProfileId, and "Meal plan*"
                  if (!key.startsWith('_bundle') && !key.startsWith('_variant') && !key.startsWith('_mealPlan') && !key.startsWith('_petProfile') && !key.includes('Meal plan')) {
                    cleanProperties[key] = item.properties[key];
                  }
                });
              }

              variantMap.set(variantId, {
                id: variantId,
                quantity: item.quantity,
                properties: Object.keys(cleanProperties).length > 0 ? cleanProperties : undefined,
                selling_plan: null  // Explicit null signals one-time (skip intercept)
              });
            }
          });

          // Reverse to preserve original cart order (Shopify /cart/add.js prepends items)
          const itemsToAdd = Array.from(variantMap.values()).reverse();

          // Step 1: Clear cart completely
          return fetch('/cart/clear.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
          })
          .then(res => res.json())
          .then(clearedCart => {
            // Verify cart is empty
            if (clearedCart.item_count !== 0) {
              throw new Error('Failed to clear cart');
            }

            // Step 2: Re-add all items atomically WITHOUT selling_plan
            return fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ items: itemsToAdd })
            });
          })
          .then(res => res.json());
        })
        .then(() => {
          this.resetConversionState();
          CartUtils.announceToScreenReader('Converted to one-time purchase.');
          if (callback) {
            callback();
          } else {
            // No callback - dispatch events to rebuild cart
            document.dispatchEvent(new CustomEvent('cart:build', { bubbles: true }));
            document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
          }
        })
        .catch(error => {
          console.error('Error removing subscription:', error);
          this.resetConversionState();
          Toast.error('There was an error updating your cart. Please try again.');
          CartUtils.rebuildCart();
        });
    },

    // Update price display with subscription discount
    updatePriceDisplay: function(showPreview = false) {
      CartUtils.fetchCart()
        .then(cart => {
          const onetimePriceEl = document.querySelector('[data-cart-onetime-price]');
          const subscriptionPriceEl = document.querySelector('[data-cart-subscription-price]');
          const subtotalEl = document.querySelector('[data-subtotal]');

          // Subtotal element is required, subscription selector elements are optional
          if (!subtotalEl) {
            return;
          }

          // USD: Simple display - no strikethrough, no complex calculations
          // Subscription discount is 0% (baked into catalog), FRESHSTART handles first order at checkout
          if (cart.currency === 'USD') {
            if (subscriptionPriceEl) subscriptionPriceEl.textContent = '';
            if (onetimePriceEl) onetimePriceEl.textContent = '';

            // Calculate recurring total (original_line_price excludes FRESHSTART discount)
            let recurringTotal = 0;
            cart.items.forEach(item => {
              recurringTotal += item.original_line_price;
            });

            // USD: Show recurring total (FRESHSTART discount only visible at checkout)
            subtotalEl.textContent = CartUtils.formatMoney(recurringTotal);

            // USD: Update first order savings (10% of recurring total)
            const firstOrderSavingsEl = document.querySelector('[data-first-order-savings]');
            if (firstOrderSavingsEl) {
              const savings = Math.round(recurringTotal * 0.10);
              firstOrderSavingsEl.textContent = '- ' + CartUtils.formatMoney(savings);
            }

            return; // Skip all CAD logic
          }

          // Calculate original total (before subscription discount)
          let onetimeTotal = 0;
          cart.items.forEach(item => {
            onetimeTotal += item.original_line_price;
          });

          // Get compare_at total from data attribute (set by Liquid on page load)
          const compareAtTotal = parseFloat(subtotalEl.dataset.compareAtTotal || 0);
          const hasCompareAtSavings = subtotalEl.dataset.hasCompareSavings === 'true';

          // Check if USD
          const isUSD = cart.currency === 'USD';

          // Calculate subscription total (with discount)
          let subscriptionTotal = 0;
          let usdCompareAtTotal = 0;
          let hasSubscription = false;
          cart.items.forEach(item => {
            if (item.selling_plan_allocation) {
              hasSubscription = true;
              // Shopify applies discount, use final_line_price
              subscriptionTotal += item.final_line_price;
              // USD: Track compare_at total for first order calculation
              if (isUSD && item.selling_plan_allocation.compare_at_price) {
                usdCompareAtTotal += item.selling_plan_allocation.compare_at_price * item.quantity;
              }
            } else {
              subscriptionTotal += item.line_price;
            }
          });
          // USD: First order = 25% off compare_at (original) price
          const usdFirstOrderTotal = Math.round(usdCompareAtTotal * 0.75);

          // If showing preview but no subscription applied yet, calculate discount (15% US, 5% CA)
          var discountRate = window.theme?.tnsSettings?.subscriptionDiscount || 0.95;
          if (showPreview && !hasSubscription) {
            subscriptionTotal = Math.floor(onetimeTotal * discountRate); // discount rounded down to nearest cent
            hasSubscription = true; // Treat as subscription for display purposes
          }

          // Update subscription selector displays (if elements exist)
          if (onetimePriceEl) {
            onetimePriceEl.textContent = ''; // Don't show price next to one-time label
          }

          // Calculate savings for display
          if (onetimeTotal > 0) {
            // Calculate savings based on floored subscription total to match actual discount
            const virtualSubscriptionTotal = Math.floor(onetimeTotal * discountRate);
            const savings = onetimeTotal - virtualSubscriptionTotal;

            // Update subscription price element if it exists
            if (subscriptionPriceEl) {
              subscriptionPriceEl.textContent = '(Save ' + CartUtils.formatMoney(savings) + ')';
              subscriptionPriceEl.style.color = '#0a8a8f'; // WCAG AA compliant - 4.52:1 contrast ratio
              subscriptionPriceEl.style.marginLeft = '8px';
            }

            if (hasSubscription) {
              // Calculate subscription compare_at total from selling_plan_allocation
              let subscriptionCompareAtTotal = 0;
              cart.items.forEach(item => {
                if (item.selling_plan_allocation && item.selling_plan_allocation.compare_at_price) {
                  subscriptionCompareAtTotal += item.selling_plan_allocation.compare_at_price * item.quantity;
                } else {
                  subscriptionCompareAtTotal += item.line_price;
                }
              });

              const actualSavings = subscriptionCompareAtTotal - subscriptionTotal;

              // USD: Show recurring price (original_line_price) - FRESHSTART discount only at checkout
              if (isUSD) {
                if (subscriptionPriceEl) {
                  subscriptionPriceEl.textContent = '';
                }
                subtotalEl.textContent = CartUtils.formatMoney(onetimeTotal);
              } else if (actualSavings > 0) {
                // CAD: Show discount with strikethrough if there are savings
                if (subscriptionPriceEl) {
                  subscriptionPriceEl.textContent = '(Save ' + CartUtils.formatMoney(actualSavings) + ')';
                }
                // Show subscription compare_at total as strikethrough with subscription total
                subtotalEl.innerHTML = '<span style="text-decoration: line-through; color: #767676; margin-right: 8px;">' + CartUtils.formatMoney(subscriptionCompareAtTotal) + '</span>' + CartUtils.formatMoney(subscriptionTotal);
              } else if (showPreview) {
                // Only show preview when explicitly requested (when user clicks subscribe)
                const previewTotal = Math.floor(onetimeTotal * discountRate); // Round down to nearest cent
                const previewSavings = onetimeTotal - previewTotal;
                if (subscriptionPriceEl) {
                  subscriptionPriceEl.textContent = '(Save ' + CartUtils.formatMoney(previewSavings) + ')';
                }
                // Show compare_at total as strikethrough with preview total
                const basePrice = hasCompareAtSavings ? compareAtTotal : onetimeTotal;
                subtotalEl.innerHTML = '<span style="text-decoration: line-through; color: #767676; margin-right: 8px;">' + CartUtils.formatMoney(basePrice) + '</span>' + CartUtils.formatMoney(previewTotal);
              } else {
                // Subscription exists but no discount and not previewing - show compare_at total with strikethrough
                if (subscriptionPriceEl) {
                  subscriptionPriceEl.textContent = '';
                }
                const basePrice = hasCompareAtSavings ? compareAtTotal : onetimeTotal;
                subtotalEl.innerHTML = '<span style="text-decoration: line-through; color: #767676; margin-right: 8px;">' + CartUtils.formatMoney(basePrice) + '</span>' + CartUtils.formatMoney(onetimeTotal);
              }
            } else {
              // No subscription active - show regular price (no virtual pricing)
              subtotalEl.textContent = CartUtils.formatMoney(onetimeTotal);
            }
          } else {
            // Cart is empty or loading
            if (subscriptionPriceEl) {
              subscriptionPriceEl.textContent = '';
            }
            subtotalEl.textContent = CartUtils.formatMoney(onetimeTotal);
          }
        })
        .catch(error => console.error('Error updating price display:', error));
    },

    // Check if cart has subscription and restore UI state
    restoreSubscriptionState: function() {
      // Skip if a conversion is in progress (cart may be in mid-transition state)
      if (this.conversionInProgress) {
        return;
      }

      CartUtils.fetchCart()
        .then(cart => {
          // Skip if cart is empty (may be mid-transition during clear + re-add)
          if (!cart.items || cart.items.length === 0) {
            return;
          }

          const subscriptionRadio = document.querySelector('.cart__plan-option--subscription input[type="radio"]');
          const onetimeRadio = document.querySelector('.cart__plan-option--onetime input[type="radio"]');
          const planDetails = document.querySelector('.cart__plan-details');
          const frequencySelect = document.querySelector('.cart__plan-frequency');

          if (!subscriptionRadio || !onetimeRadio || !planDetails || !frequencySelect) {
            return;
          }

          // Check if any item has a selling plan
          const hasSubscription = cart.items.some(item => item.selling_plan_allocation);


          if (hasSubscription) {
            // Read the plan from the first SUBSCRIBED item, not cart.items[0].
            // A one-time add-on can sit first in the cart (e.g. add a bone broth
            // via "one time" to a subscribed meal box); items[0] then has no
            // selling_plan_allocation, sellingPlanId is undefined, nothing matches,
            // and the selector silently fell back to the 4-week default while the
            // meals were on a different plan. Same class as the boxPacker
            // lineItems[0] bug (OPSAPPS-64): indexing position instead of finding
            // the item that actually carries the data.
            const subscribedItem = cart.items.find(item => item.selling_plan_allocation);
            const sellingPlanId = subscribedItem?.selling_plan_allocation?.selling_plan?.id;

            // Find the frequency that matches this selling plan
            let selectedFrequency = '4'; // default to 4 weeks
            Object.keys(CartUtils.SELLING_PLANS).forEach(freq => {
              if (CartUtils.SELLING_PLANS[freq] === String(sellingPlanId)) {
                selectedFrequency = freq;
              }
            });

            // Set UI to subscription state
            subscriptionRadio.checked = true;
            onetimeRadio.checked = false;
            planDetails.style.display = 'block';
            frequencySelect.value = selectedFrequency;


            // Update price display to show actual subscription pricing
            this.updatePriceDisplay(false);
          } else {
            // Set UI to one-time state
            onetimeRadio.checked = true;
            subscriptionRadio.checked = false;
            planDetails.style.display = 'none';


            // Update price display to show one-time pricing
            this.updatePriceDisplay(false);
          }
        })
        .catch(error => console.error('Error restoring subscription state:', error));
    },

    // USD Mode: Subscription-only, auto-apply subscription with selected frequency
    initUsdMode: function(frequencySelect) {
      // Check current cart subscription state
      CartUtils.fetchCart().then(cart => {
        const hasSubscription = cart.items.some(item => item.selling_plan_allocation);
        const selectedFrequency = frequencySelect.value;

        // If cart already has subscription, sync dropdown to show actual frequency from cart
        if (hasSubscription) {
          // Get the selling plan ID from first subscription item
          const itemWithSub = cart.items.find(item => item.selling_plan_allocation);
          const sellingPlanId = itemWithSub?.selling_plan_allocation?.selling_plan?.id;

          if (sellingPlanId) {
            // Find the frequency that matches this selling plan
            let actualFrequency = '4'; // default
            Object.keys(CartUtils.SELLING_PLANS).forEach(freq => {
              if (CartUtils.SELLING_PLANS[freq] === String(sellingPlanId)) {
                actualFrequency = freq;
              }
            });

            // Update dropdown to show actual frequency
            frequencySelect.value = actualFrequency;
          }

          this.updatePriceDisplay(false);
          return;
        }

        // Apply subscription with selected frequency (default 4 weeks)
        const frequency = selectedFrequency || '4';
        if (cart.items.length > 0) {
          this.applySubscription(frequency, () => {
            document.dispatchEvent(new CustomEvent('cart:build', { bubbles: true }));
            document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
            this.updatePriceDisplay(false);
          });
        }
      });

      // Attach frequency change listener for USD mode
      if (!this.usdListenersAttached) {
        this.usdListenersAttached = true;

        document.addEventListener('change', (e) => {
          if (e.target.matches('.cart__plan-frequency')) {
            const frequency = e.target.value;
            if (frequency) {
              this.applySubscription(frequency, () => {
                document.dispatchEvent(new CustomEvent('cart:build', { bubbles: true }));
                document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
                this.updatePriceDisplay(false);
              });
            }
          }
        });
      }
    },

    usdListenersAttached: false,

    // Track if listeners have been attached to prevent duplicates
    listenersAttached: false,

    init: function() {
      const subscriptionRadio = document.querySelector('.cart__plan-option--subscription input[type="radio"]');
      const onetimeRadio = document.querySelector('.cart__plan-option--onetime input[type="radio"]');
      const planDetails = document.querySelector('.cart__plan-details');
      const frequencySelect = document.querySelector('.cart__plan-frequency');

      // Check if USD mode (subscription-only, no one-time option)
      const subscriptionSelector = document.querySelector('.cart__subscription-selector');
      const isUsdMode = subscriptionSelector && subscriptionSelector.dataset.usdMode === 'true';

      // For USD mode, only need subscription radio and frequency select
      if (isUsdMode) {
        if (!frequencySelect) {
          return;
        }
        // USD: Auto-apply subscription with selected frequency
        this.initUsdMode(frequencySelect);
        return;
      }

      // CAD mode: require all elements
      if (!subscriptionRadio || !onetimeRadio || !planDetails || !frequencySelect) {
        return;
      }

      // Restore subscription state from cart (this will also update price display)
      this.restoreSubscriptionState();

      // Only attach event listeners once to prevent duplicates
      // Listeners are attached to document and use event delegation
      if (this.listenersAttached) {
        return;
      }
      this.listenersAttached = true;

      // Use event delegation on document to handle dynamically replaced elements
      document.addEventListener('change', (e) => {
        // Handle subscription radio selection
        if (e.target.matches('.cart__plan-option--subscription input[type="radio"]')) {
          const currentPlanDetails = document.querySelector('.cart__plan-details');
          const currentFrequencySelect = document.querySelector('.cart__plan-frequency');

          if (e.target.checked && currentPlanDetails && currentFrequencySelect) {
            currentPlanDetails.style.display = 'block';
            // Show 5% discount preview immediately
            this.updatePriceDisplay(true);

            // If a frequency is already selected, apply it to cart
            if (currentFrequencySelect.value) {
              this.applySubscription(currentFrequencySelect.value, () => {
                // Trigger cart events to rebuild cart drawer HTML
                document.dispatchEvent(new CustomEvent('cart:build', { bubbles: true }));
                document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));

                // Update price display to show actual subscription pricing (not preview)
                this.updatePriceDisplay(false);
              });
            }
          }
        }

        // Handle one-time radio selection
        if (e.target.matches('.cart__plan-option--onetime input[type="radio"]')) {
          const currentPlanDetails = document.querySelector('.cart__plan-details');
          const onetimeRadio = e.target;
          const subscriptionRadio = document.querySelector('.cart__plan-option--subscription input[type="radio"]');

          if (e.target.checked && currentPlanDetails) {
            // Check if cart has meal plan items and extract pet name
            CartUtils.fetchCart().then(cart => {
              let petName = '';
              const hasMealPlan = cart.items.some(item => {
                if (!item.properties) return false;
                const mealPlanKey = Object.keys(item.properties).find(key => key.includes('Meal plan'));
                if (mealPlanKey) {
                  // Extract pet name from "Meal plan for <petname>"
                  const match = mealPlanKey.match(/Meal plan for (.+)/);
                  if (match) petName = match[1];
                  return true;
                }
                return false;
              });

              const proceedWithOneTime = () => {
                currentPlanDetails.style.display = 'none';
                this.removeSubscription(() => {
                  document.dispatchEvent(new CustomEvent('cart:build', { bubbles: true }));
                  document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
                  this.updatePriceDisplay(false);
                });
              };

              if (hasMealPlan) {
                Toast.confirm("By changing the parameters of a meal plan, it is no longer our recommendation and must be removed.\n\nAll items remaining in your cart will no longer be part of a meal plan.", {
                  confirmText: 'Continue',
                  cancelText: 'Cancel',
                  type: 'warning'
                }).then(accepted => {
                  if (accepted) {
                    // On cart page, reload after one-time conversion to update Liquid-rendered headers
                    const isCartPage = document.querySelector('.template-cart');
                    if (isCartPage) {
                      currentPlanDetails.style.display = 'none';
                      this.removeSubscription(() => {
                        window.location.reload();
                      });
                    } else {
                      proceedWithOneTime();
                    }
                  } else {
                    // Revert to subscription radio
                    if (subscriptionRadio) subscriptionRadio.checked = true;
                    currentPlanDetails.style.display = 'block';
                  }
                });
              } else {
                proceedWithOneTime();
              }
            });
          }
        }

        // Handle frequency select changes
        if (e.target.matches('.cart__plan-frequency')) {
          const currentSubscriptionRadio = document.querySelector('.cart__plan-option--subscription input[type="radio"]');

          if (currentSubscriptionRadio && currentSubscriptionRadio.checked && e.target.value) {
            const frequencySelect = e.target;
            const newFrequency = e.target.value;
            const previousFrequency = frequencySelect.dataset.previousValue || '';

            // Store current value for potential revert
            frequencySelect.dataset.previousValue = newFrequency;

            // Check if cart has meal plan items or mixed one-time/subscription items
            CartUtils.fetchCart().then(cart => {
              const hasMealPlan = cart.items.some(item => {
                if (!item.properties) return false;
                return Object.keys(item.properties).some(key => key.includes('Meal plan'));
              });

              // Check for mixed cart (both one-time and subscription items)
              const hasSubscriptionItems = cart.items.some(item => item.selling_plan_allocation);
              const hasOnetimeItems = cart.items.some(item => !item.selling_plan_allocation);
              const hasMixedCart = hasSubscriptionItems && hasOnetimeItems;

              const proceedWithFrequencyChange = () => {
                this.applySubscription(newFrequency, () => {
                  document.dispatchEvent(new CustomEvent('cart:build', { bubbles: true }));
                  document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
                  this.updatePriceDisplay(false);
                });
              };

              // Show warning for meal plan items - frequency change removes the meal plan
              if (hasMealPlan) {
                Toast.confirm("By changing the parameters of a meal plan, it is no longer our recommendation and must be removed.\n\nAll items remaining in your cart will no longer be part of a meal plan.", {
                  confirmText: 'Continue',
                  cancelText: 'Cancel',
                  type: 'warning'
                }).then(accepted => {
                  if (accepted) {
                    // On cart page, reload after frequency change to update Liquid-rendered headers
                    const isCartPage = document.querySelector('.template-cart');
                    if (isCartPage) {
                      this.applySubscription(newFrequency, () => {
                        window.location.reload();
                      });
                    } else {
                      proceedWithFrequencyChange();
                    }
                  } else {
                    // Revert to previous frequency
                    frequencySelect.value = previousFrequency;
                  }
                });
              } else if (hasMixedCart) {
                // Show warning for mixed cart (one-time items will become subscriptions)
                Toast.confirm("Changing the delivery frequency will apply subscription to all items in your cart.", {
                  confirmText: 'Confirm',
                  cancelText: 'Cancel',
                  type: 'warning'
                }).then(accepted => {
                  if (accepted) {
                    proceedWithFrequencyChange();
                  } else {
                    // Revert to previous frequency
                    frequencySelect.value = previousFrequency;
                  }
                });
              } else {
                proceedWithFrequencyChange();
              }
            });
          }
        }
      });
    },

    saveDrawerState: function() {
      // Check if cart drawer is currently open
      const cartDrawer = document.getElementById('CartDrawer');
      if (cartDrawer && cartDrawer.classList.contains('drawer--is-open')) {
        sessionStorage.setItem('cartDrawerWasOpen', 'true');
      }
    },

    // Update subscription selector visibility based on cart state
    // Hide when: cart is empty, cart is Trial Boxes only, OR multiple meal plans
    // in cart (different frequencies not supported).
    // NOTE: queries ALL selectors — the cart page and the drawer each render one,
    // and the previous single querySelector only ever touched the first.
    updateVisibility: function() {
      const selectors = document.querySelectorAll('.cart__subscription-selector');
      if (!selectors.length) return;
      const setAll = (value) => selectors.forEach(function (el) { el.style.display = value; });

      CartUtils.fetchCart()
        .then(cart => {
          if (cart.item_count === 0) {
            setAll('none');
            return;
          }

          // OPSAPPS-75: Trial Boxes are one-time purchase only, so a cart holding
          // nothing but trial boxes has nothing that can recur. Without this the
          // selector is re-shown here on every cart:updated, overriding both the
          // Liquid render and the MealTally toggle.
          if (CartUtils.subscriptionUnavailable(cart)) {
            setAll('none');
            return;
          }

          // Count unique meal plan IDs in cart
          const mealPlanIds = new Set();
          cart.items.forEach(item => {
            const mealPlanId = item.properties?._mealPlanId;
            if (mealPlanId) {
              mealPlanIds.add(mealPlanId);
            }
          });

          // Hide subscription toggle if multiple meal plans (they may have different frequencies)
          if (mealPlanIds.size > 1) {
            setAll('none');
            console.log('[SubscriptionSelector] Hidden: multiple meal plans in cart (' + mealPlanIds.size + ')');
          } else {
            setAll('block');
          }
        })
        .catch(error => console.error('[SubscriptionSelector] Error updating visibility:', error));
    },

    initEvents: function() {
      // Initialize on page load
      if (document.querySelector('.cart__subscription-selector')) {
        this.init();
        this.updateVisibility();
      } else {
        // No subscription selector (below 14lb) - still need to update price display for virtual subscription
        this.updatePriceDisplay(false);
      }

      // Re-initialize when cart is rebuilt (DOM has been replaced)
      document.addEventListener('cart:built', () => {
        setTimeout(() => {
          if (document.querySelector('.cart__subscription-selector')) {
            this.init();
            this.updateVisibility();
          } else {
            this.updatePriceDisplay(false);
          }
        }, 200);
      });

      // Also update visibility, state, and price display when cart updates
      document.addEventListener('cart:updated', () => {
        const selector = document.querySelector('.cart__subscription-selector');
        if (selector) {
          this.updateVisibility();
          this.restoreSubscriptionState();
        } else {
          this.updatePriceDisplay(false);
        }
      });

      // Update price display when drawer opens (to show virtual subscription pricing if below 14lb)
      document.addEventListener('drawerOpen', () => {
        setTimeout(() => this.updatePriceDisplay(false), 100);
      });
    }
  };

  // =============================================================================
  // Item Remover Module
  // =============================================================================
  const ItemRemover = {
    handleRemoveClick: async function(e) {
      const button = e.target.closest('.js-cart-remove');
      if (!button) return;

      const key = button.dataset.key;
      if (!key) {
        console.warn('[Cart Item Remove] No key found on button');
        return;
      }

      // Fetch cart to check if item is part of a meal plan or bundle
      const cart = await CartUtils.fetchCart();
      const itemToRemove = cart.items.find(item => item.key === key);
      const isMealPlan = itemToRemove?.properties?._mealPlanId;
      const itemBundleUid = itemToRemove?.properties?._bundleUid;

      // Check if this is a singleton in its bundle (only item with this bundleUid)
      let isSingleton = false;
      let isBundle = false;
      if (itemBundleUid) {
        const bundleSiblings = cart.items.filter(item =>
          item.properties?._bundleUid === itemBundleUid && item.key !== key
        );
        isSingleton = bundleSiblings.length === 0;
        isBundle = !isSingleton; // Only treat as bundle if there are other items in the same bundle
      }

      // Show appropriate confirmation toast
      const itemTitle = itemToRemove?.product_title || 'this item';
      let message;
      if (isMealPlan) {
        message = "By changing the parameters of a meal plan, it is no longer our recommendation and must be removed.\n\nAll items remaining in your cart will no longer be part of a meal plan.";
      } else if (isBundle) {
        message = `Remove "${itemTitle}" from your bundle?\n\nThis will convert your remaining items to individual purchases (subscription discount will be kept).`;
      } else {
        // Regular item or singleton in bundle - simple removal message
        message = `Remove "${itemTitle}" from your cart?`;
      }

      const confirmed = await Toast.confirm(message, {
        confirmText: 'Remove',
        cancelText: 'Cancel',
        type: 'warning'
      });

      if (!confirmed) return;

      // Update cart to set quantity to 0 (removes item)
      const updatedCart = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: key,
          quantity: 0
        })
      }).then(res => res.json());

      // Announce removal to screen readers
      CartUtils.announceToScreenReader(itemTitle + ' removed from cart');

      // If item was part of a meal plan and there are remaining items, strip meal-plan attrs.
      // BAB self-serve bundles (isBundle without isMealPlan) keep their attributes — the
      // remaining items stay a coherent (smaller) bundle, so no rebuild is needed.
      if (isMealPlan && updatedCart.items.length > 0) {
        await this.removeBundleAttributesFromCart(updatedCart, isMealPlan);
        return;
      }

      // Check if we're in cart drawer or cart page
      const isCartPage = document.querySelector('.template-cart');

      if (isCartPage) {
        // Cart page - save drawer state and reload to show updated cart
        SubscriptionSelector.saveDrawerState();
        window.location.reload();
      } else {
        // Cart drawer - check if cart is now empty
        if (updatedCart.item_count === 0) {
          // Dispatch cart:updated so cart-state-sync can revert buttons to "add to cart"
          document.dispatchEvent(new CustomEvent('cart:updated', {
            bubbles: true,
            detail: { cart: updatedCart }
          }));
          document.dispatchEvent(new CustomEvent('cart:close'));
        } else {
          // Cart still has items - trigger rebuild without reload
          CartUtils.rebuildCart();
        }
      }
    },

    // Remove meal plan / bundle attributes from cart items
    // If mealPlanId is provided, only strips from items with that _mealPlanId
    removeBundleAttributesFromCart: async function(cart, mealPlanId) {
      console.log('[ItemRemover] Removing meal plan attributes, mealPlanId:', mealPlanId);
      console.log('[ItemRemover] Cart items before:', cart.items.map(i => ({ title: i.title, props: i.properties })));

      // Clear cart first
      await fetch('/cart/clear.js', { method: 'POST' });

      // Editing a meal plan drops the MPQ *recommendation* (the _mealPlanId / pet-profile
      // metadata), but the remaining meals must STAY a coherent subscription bundle. Otherwise
      // Awtomic has no _bundleUid to group them and the Pet Meal Box minimum ("Select at least
      // N meal packages") stops being enforced — the meals become loose recurring lines the
      // customer can drop below the order minimum (root cause of the orphaned-subscription bug).
      // So we re-stamp a fresh, shared _bundle* grouping on the remaining primary-meal
      // subscription lines, mirroring the BAB self-serve path (which already keeps a coherent
      // bundle on removal). Add-ons / one-time lines stay standalone.
      const rebundleUid = CartUtils.generateBundleUid();
      const rebundleBoxSize = cart.items.filter(function(i) {
        const im = i.properties && i.properties._mealPlanId;
        const strip = mealPlanId ? (im === mealPlanId) : true;
        return strip && i.selling_plan_allocation && CartUtils.shouldBundleVariant(i.variant_id);
      }).length;

      // Re-add items, converting the edited meal plan into a coherent self-serve bundle
      const items = cart.items.map(item => {
        const cleanItem = {
          id: item.variant_id,
          quantity: item.quantity
        };

        // Keep selling plan if exists
        if (item.selling_plan_allocation) {
          cleanItem.selling_plan = item.selling_plan_allocation.selling_plan.id;
        }

        // Check if this item should have its meal plan attributes stripped
        const itemMealPlanId = item.properties?._mealPlanId;
        const shouldStripMealPlan = mealPlanId ? (itemMealPlanId === mealPlanId) : true;

        console.log('[ItemRemover] Item:', item.title, 'itemMealPlanId:', itemMealPlanId, 'shouldStrip:', shouldStripMealPlan);

        // Build clean properties (the bypass flag belongs at the request top-level,
        // not on individual line items — Cart Intercept gates on it and strips it
        // before forwarding to Shopify, so it never persists on order line items)
        const cleanProperties = {};

        if (item.properties) {
          Object.keys(item.properties).forEach(key => {
            if (shouldStripMealPlan) {
              // Drop the recommendation metadata (_mealPlan*, _petProfile*, _petName,
              // "Meal plan*") AND the stale _bundle*/_variant* props — a fresh coherent
              // bundle is re-stamped below so the box grouping / minimum survives the edit.
              if (!key.startsWith('_bundle') && !key.startsWith('_variant') && !key.startsWith('_mealPlan') && !key.startsWith('_petProfile') && !key.startsWith('_petName') && !key.includes('Meal plan')) {
                cleanProperties[key] = item.properties[key];
              }
            } else {
              // Keep all properties for items not in the target meal plan
              cleanProperties[key] = item.properties[key];
            }
          });
        }

        // Re-stamp a coherent bundle grouping on the remaining primary-meal subscription
        // lines so the Pet Meal Box minimum stays enforceable (Awtomic groups by _bundleUid).
        // Only bundle-eligible meals on a selling plan qualify; add-ons / one-time lines
        // stay standalone — same gate as buildAddToCartPayload.
        if (shouldStripMealPlan && item.selling_plan_allocation && CartUtils.shouldBundleVariant(item.variant_id)) {
          Object.assign(
            cleanProperties,
            CartUtils.buildBundleProperties(item.variant_id, rebundleUid),
            { _bundleBoxSize: rebundleBoxSize }
          );
        }

        cleanItem.properties = cleanProperties;
        console.log('[ItemRemover] Clean item properties:', cleanProperties);

        return cleanItem;
      });

      // Reverse to preserve original cart order (Shopify /cart/add.js prepends items)
      items.reverse();

      console.log('[ItemRemover] Adding items back:', items);

      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items, _bypassSubscriptionIntercept: true })
      });

      // Save drawer state and reload page to show updated cart
      SubscriptionSelector.saveDrawerState();
      window.location.reload();
    },

    init: function() {
      // Listen for clicks on remove buttons
      document.addEventListener('click', (e) => this.handleRemoveClick(e));
    }
  };

  // =============================================================================
  // Meal Box Adder Module
  // =============================================================================
  const MealBoxAdder = {
    addToCart: function(button) {
      const variantId = button.dataset.variantId;

      if (!variantId) {
        console.error('No variant ID found');
        return;
      }

      // Disable button and show loading state
      button.disabled = true;
      button.classList.add('adding');
      const originalText = button.innerHTML;
      button.innerHTML = 'Adding...';

      // Fetch cart and add item with appropriate payload
      CartUtils.fetchCart()
        .then(cart => {
          const itemPayload = CartUtils.buildAddToCartPayload(variantId, cart);
          return fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: [itemPayload] })
          });
        })
        .then(response => {
          if (!response.ok) {
            throw new Error('Failed to add to cart');
          }
          return response.json();
        })
        .then(data => {
          // Success! Update button state
          button.innerHTML = 'Added! ✓';
          button.classList.remove('adding');

          // Dispatch the same event that product forms use
          // This triggers the cart drawer to rebuild and open automatically
          document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
            detail: { product: data, addToCartBtn: button }
          }));

          // Reset button after 2 seconds
          setTimeout(() => {
            button.innerHTML = originalText;
            button.disabled = false;
          }, 2000);

          // Dispatch custom event for other scripts
          document.dispatchEvent(new CustomEvent('cart:item-added', {
            detail: { variantId, data }
          }));

          // Announce addition to screen readers
          var productTitle = button.dataset.productTitle || 'Item';
          CartUtils.announceToScreenReader(productTitle + ' added to cart');
        })
        .catch(error => {
          console.error('Error adding to cart:', error);
          button.innerHTML = 'Error - Try Again';
          button.classList.remove('adding');

          setTimeout(() => {
            button.innerHTML = originalText;
            button.disabled = false;
          }, 2000);
        });
    },

    init: function() {
      // Handle all add-to-meal-box buttons
      document.addEventListener('click', (e) => {
        const button = e.target.closest('.add-to-meal-box-btn');
        if (!button) return;

        e.preventDefault();
        this.addToCart(button);
      });
    }
  };

  // =============================================================================
  // Grid Button Sync Module
  // Manages dynamic add-to-cart buttons on product grids (.a2c_dyn_button)
  // Consolidated from cart-state-sync.js
  // =============================================================================
  const GridButtonSync = {
    // Update all dynamic buttons based on current cart state
    syncAllButtons: function(cart) {
      if (!cart) return;

      // Create a map of variant_id => cart line data
      const cartMap = {};
      cart.items.forEach((item, index) => {
        cartMap[item.variant_id] = {
          line: index + 1, // Shopify uses 1-based line numbers
          quantity: item.quantity
        };
      });

      // Update all dynamic buttons
      const allButtons = document.querySelectorAll('.a2c_dyn_button');

      allButtons.forEach(button => {
        const variantId = parseInt(button.dataset.variantId);
        const cartData = cartMap[variantId];

        if (cartData) {
          this.showQuantityController(button, cartData.line, cartData.quantity);
        } else {
          this.showAddToCartButton(button);
        }
      });
    },

    // Show quantity controller, hide add to cart button
    showQuantityController: function(button, line, quantity) {
      const addUI = button.querySelector('.add2cart_ui');
      const quantityUI = button.querySelector('.cart_quantity_ui');

      if (!addUI || !quantityUI) return;

      addUI.style.display = 'none';
      quantityUI.style.display = 'flex';

      const minusBtn = quantityUI.querySelector('[data-action="decrease-quantity"]');
      const plusBtn = quantityUI.querySelector('[data-action="increase-quantity"]');
      const input = quantityUI.querySelector('.quantity-selector__value');
      const removeBtn = quantityUI.querySelector('.cart__remove');

      if (minusBtn) {
        minusBtn.dataset.line = line;
        minusBtn.dataset.quantity = Math.max(0, quantity - 1);
      }
      if (plusBtn) {
        plusBtn.dataset.line = line;
        plusBtn.dataset.quantity = quantity + 1;
      }
      if (input) {
        input.dataset.line = line;
        input.value = quantity;
        input.dataset.currentValue = quantity;
      }
      if (removeBtn) {
        removeBtn.dataset.line = line;
        removeBtn.href = `/cart/change?quantity=0&line=${line}`;
      }
    },

    // Show add to cart button, hide quantity controller
    showAddToCartButton: function(button) {
      const addUI = button.querySelector('.add2cart_ui');
      const quantityUI = button.querySelector('.cart_quantity_ui');

      if (!addUI || !quantityUI) return;

      addUI.style.display = 'block';
      quantityUI.style.display = 'none';
    },

    // Emit cart updated events
    emitCartUpdated: function(cart, shouldOpenDrawer) {
      if (shouldOpenDrawer) {
        document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
          bubbles: true,
          detail: { product: cart, cart: cart, source: 'grid-button-sync' }
        }));
      } else {
        document.dispatchEvent(new CustomEvent('cart:build', {
          bubbles: true,
          detail: { cart: cart, source: 'grid-button-sync' }
        }));
      }

      document.dispatchEvent(new CustomEvent('cart:updated', {
        bubbles: true,
        detail: { cart: cart, source: 'grid-button-sync' }
      }));
    },

    // Handle add to cart button clicks
    handleAddToCart: function(event) {
      const button = event.target.closest('[data-action="add-to-cart-button"]');
      if (!button || button.disabled) return;

      event.preventDefault();

      const variantId = button.dataset.variantId;
      button.classList.add('btn--loading');

      // Fetch cart and add item with appropriate payload
      CartUtils.fetchCart()
        .then(cart => {
          const itemPayload = CartUtils.buildAddToCartPayload(variantId, cart);
          return fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: [itemPayload] })
          });
        })
        .then(res => res.json())
        .then(() => {
          button.classList.remove('btn--loading');
          return CartUtils.fetchCart();
        })
        .then(cart => {
          if (cart) {
            this.syncAllButtons(cart);
            this.emitCartUpdated(cart, true);
          }
        })
        .catch(error => {
          console.error('[GridButtonSync] Error adding to cart:', error);
          button.classList.remove('btn--loading');
        });
    },

    // Handle quantity change button clicks
    handleQuantityChange: async function(event) {
      const button = event.target.closest('[data-action]');
      if (!button) return;

      const action = button.dataset.action;
      if (action !== 'increase-quantity' && action !== 'decrease-quantity') return;

      event.preventDefault();

      const line = parseInt(button.dataset.line);
      const quantity = parseInt(button.dataset.quantity);

      // Check if item is part of a meal plan
      const cart = await CartUtils.fetchCart();
      const item = cart.items[line - 1]; // lines are 1-indexed
      const isMealPlan = item?.properties?._mealPlanId;

      if (isMealPlan) {
        const confirmed = await Toast.confirm(
          "By changing the parameters of a meal plan, it is no longer our recommendation and must be removed.\n\nAll items remaining in your cart will no longer be part of a meal plan.",
          {
            confirmText: 'Continue',
            cancelText: 'Cancel',
            type: 'warning'
          }
        );

        if (!confirmed) return;

        // Update quantity in cart first, then remove meal plan attributes from this meal plan only
        const updatedCart = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ line, quantity })
        }).then(res => res.json());

        await this.removeBundleAttributesFromCart(updatedCart, isMealPlan);
        return;
      }

      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line, quantity })
      })
        .then(res => res.json())
        .then(cart => {
          this.syncAllButtons(cart);
          this.emitCartUpdated(cart, false);
        })
        .catch(error => {
          console.error('[GridButtonSync] Error updating cart:', error);
        });
    },

    // Handle remove button clicks
    handleRemove: async function(event) {
      const button = event.target.closest('[data-action="remove-from-cart"]');
      if (!button) return;

      event.preventDefault();

      const line = parseInt(button.dataset.line);

      // Fetch cart to check if item is part of a meal plan or bundle
      const cart = await CartUtils.fetchCart();
      const itemToRemove = cart.items[line - 1]; // lines are 1-indexed
      const isMealPlan = itemToRemove?.properties?._mealPlanId;
      const isBundle = itemToRemove?.properties?._bundleUid;

      // Show appropriate confirmation toast
      const itemTitle = itemToRemove?.product_title || 'this item';
      let message;
      if (isMealPlan) {
        message = "By changing the parameters of a meal plan, it is no longer our recommendation and must be removed.\n\nAll items remaining in your cart will no longer be part of a meal plan.";
      } else if (isBundle) {
        message = `Remove "${itemTitle}" from your bundle?\n\nThis will convert your remaining items to individual purchases (subscription discount will be kept).`;
      } else {
        message = `Remove "${itemTitle}" from your cart?`;
      }

      const confirmed = await Toast.confirm(message, {
        confirmText: 'Remove',
        cancelText: 'Cancel',
        type: 'warning'
      });

      if (!confirmed) return;

      // Remove the item
      const updatedCart = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line, quantity: 0 })
      }).then(res => res.json());

      // If item was part of a meal plan and there are remaining items, strip meal-plan attrs.
      // BAB self-serve bundles (isBundle without isMealPlan) keep their attributes — the
      // remaining items stay a coherent (smaller) bundle, so no rebuild is needed.
      if (isMealPlan && updatedCart.items.length > 0) {
        await this.removeBundleAttributesFromCart(updatedCart, isMealPlan);
      } else {
        this.syncAllButtons(updatedCart);
        this.emitCartUpdated(updatedCart, false);
      }
    },

    // Remove meal plan / bundle attributes from cart items
    // If mealPlanId is provided, only strips from items with that _mealPlanId
    // If mealPlanId is null/undefined, strips from all items
    removeBundleAttributesFromCart: async function(cart, mealPlanId) {
      console.log('[GridButtonSync] Removing meal plan attributes, mealPlanId:', mealPlanId);
      console.log('[GridButtonSync] Cart items before:', cart.items.map(i => ({ title: i.title, props: i.properties })));

      // Clear cart first and verify it's empty
      const clearedCart = await fetch('/cart/clear.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      }).then(res => res.json());

      if (clearedCart.item_count !== 0) {
        console.error('[GridButtonSync] Failed to clear cart');
        return;
      }

      // Editing a meal plan drops the MPQ *recommendation* (the _mealPlanId / pet-profile
      // metadata), but the remaining meals must STAY a coherent subscription bundle. Otherwise
      // Awtomic has no _bundleUid to group them and the Pet Meal Box minimum ("Select at least
      // N meal packages") stops being enforced — the meals become loose recurring lines the
      // customer can drop below the order minimum (root cause of the orphaned-subscription bug).
      // So we re-stamp a fresh, shared _bundle* grouping on the remaining primary-meal
      // subscription lines, mirroring the BAB self-serve path (which already keeps a coherent
      // bundle on removal). Add-ons / one-time lines stay standalone.
      const rebundleUid = CartUtils.generateBundleUid();
      const rebundleBoxSize = cart.items.filter(function(i) {
        const im = i.properties && i.properties._mealPlanId;
        const strip = mealPlanId ? (im === mealPlanId) : true;
        return strip && i.selling_plan_allocation && CartUtils.shouldBundleVariant(i.variant_id);
      }).length;

      // Re-add items, converting the edited meal plan into a coherent self-serve bundle
      const items = cart.items.map(item => {
        const cleanItem = {
          id: item.variant_id,
          quantity: item.quantity
        };

        // Keep selling plan if exists
        if (item.selling_plan_allocation) {
          cleanItem.selling_plan = item.selling_plan_allocation.selling_plan.id;
        }

        // Check if this item should have its meal plan attributes stripped
        const itemMealPlanId = item.properties?._mealPlanId;
        const shouldStripMealPlan = mealPlanId ? (itemMealPlanId === mealPlanId) : true;

        console.log('[GridButtonSync] Item:', item.title, 'itemMealPlanId:', itemMealPlanId, 'shouldStrip:', shouldStripMealPlan);

        // Build clean properties (the bypass flag belongs at the request top-level,
        // not on individual line items — Cart Intercept gates on it and strips it
        // before forwarding to Shopify, so it never persists on order line items)
        const cleanProperties = {};

        if (item.properties) {
          Object.keys(item.properties).forEach(key => {
            if (shouldStripMealPlan) {
              // Drop the recommendation metadata (_mealPlan*, _petProfile*, _petName,
              // "Meal plan*") AND the stale _bundle*/_variant* props — a fresh coherent
              // bundle is re-stamped below so the box grouping / minimum survives the edit.
              if (!key.startsWith('_bundle') && !key.startsWith('_variant') && !key.startsWith('_mealPlan') && !key.startsWith('_petProfile') && !key.startsWith('_petName') && !key.includes('Meal plan')) {
                cleanProperties[key] = item.properties[key];
              }
            } else {
              // Keep all properties for items not in the target meal plan
              cleanProperties[key] = item.properties[key];
            }
          });
        }

        // Re-stamp a coherent bundle grouping on the remaining primary-meal subscription
        // lines so the Pet Meal Box minimum stays enforceable (Awtomic groups by _bundleUid).
        // Only bundle-eligible meals on a selling plan qualify; add-ons / one-time lines
        // stay standalone — same gate as buildAddToCartPayload.
        if (shouldStripMealPlan && item.selling_plan_allocation && CartUtils.shouldBundleVariant(item.variant_id)) {
          Object.assign(
            cleanProperties,
            CartUtils.buildBundleProperties(item.variant_id, rebundleUid),
            { _bundleBoxSize: rebundleBoxSize }
          );
        }

        cleanItem.properties = cleanProperties;
        console.log('[GridButtonSync] Clean item properties:', cleanProperties);

        return cleanItem;
      });

      // Reverse to preserve original cart order (Shopify /cart/add.js prepends items)
      items.reverse();

      console.log('[GridButtonSync] Adding items back:', items);

      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items, _bypassSubscriptionIntercept: true })
      });

      // On cart page, reload to update Liquid-rendered headers
      const isCartPage = document.querySelector('.template-cart');
      if (isCartPage) {
        window.location.reload();
        return;
      }

      // Cart drawer - fetch final cart state and update UI via AJAX
      const finalCart = await CartUtils.fetchCart();
      this.syncAllButtons(finalCart);
      this.emitCartUpdated(finalCart, true); // true to trigger full rebuild
    },

    // Handle direct quantity input changes (manual typing)
    handleInputChange: async function(event) {
      const input = event.target;
      if (!input.classList.contains('quantity-selector__value')) return;
      // Only handle inputs inside the grid quantity selectors
      if (!input.closest('.cart_quantity_ui')) return;

      const line = parseInt(input.dataset.line);
      let quantity = parseInt(input.value);

      // Validate input
      if (isNaN(quantity) || quantity < 0) {
        quantity = parseInt(input.dataset.currentValue) || 1;
        input.value = quantity;
        return;
      }

      // Skip if unchanged
      if (quantity === parseInt(input.dataset.currentValue)) return;

      // Check if item is part of a meal plan
      const cart = await CartUtils.fetchCart();
      const item = cart.items[line - 1]; // lines are 1-indexed
      const isMealPlan = item?.properties?._mealPlanId;

      if (isMealPlan) {
        const confirmed = await Toast.confirm(
          "By changing the parameters of a meal plan, it is no longer our recommendation and must be removed.\n\nAll items remaining in your cart will no longer be part of a meal plan.",
          {
            confirmText: 'Continue',
            cancelText: 'Cancel',
            type: 'warning'
          }
        );

        if (!confirmed) {
          // Restore previous value
          input.value = input.dataset.currentValue || 1;
          return;
        }

        // Update quantity in cart first, then remove meal plan attributes from this meal plan only
        const updatedCart = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ line, quantity })
        }).then(res => res.json());

        await this.removeBundleAttributesFromCart(updatedCart, isMealPlan);
        return;
      }

      // Add loading state
      const wrapper = input.closest('.a2c_dyn_button');
      if (wrapper) wrapper.classList.add('is-loading');

      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line, quantity })
      })
        .then(res => res.json())
        .then(cart => {
          if (wrapper) wrapper.classList.remove('is-loading');
          this.syncAllButtons(cart);
          this.emitCartUpdated(cart, false);
        })
        .catch(error => {
          console.error('[GridButtonSync] Error updating quantity:', error);
          if (wrapper) wrapper.classList.remove('is-loading');
          // Restore previous value on error
          input.value = input.dataset.currentValue || 1;
        });
    },

    // Handle external cart updates
    handleExternalCartUpdate: function(event) {
      // Avoid circular updates
      if (event.detail && event.detail.source === 'grid-button-sync') {
        return;
      }

      setTimeout(() => {
        CartUtils.fetchCart().then(cart => {
          if (cart) {
            this.syncAllButtons(cart);
          }
        });
      }, 200);
    },

    init: function() {
      // Sync on page load
      CartUtils.fetchCart().then(cart => {
        if (cart) this.syncAllButtons(cart);
      });

      // Click handlers
      document.addEventListener('click', (e) => this.handleAddToCart(e));
      document.addEventListener('click', (e) => this.handleQuantityChange(e));
      document.addEventListener('click', (e) => this.handleRemove(e));

      // Input change handler for direct quantity editing
      document.addEventListener('change', (e) => this.handleInputChange(e));

      // External cart update handlers
      document.addEventListener('ajaxProduct:added', (e) => this.handleExternalCartUpdate(e));
      document.addEventListener('cart:updated', (e) => this.handleExternalCartUpdate(e));
      document.addEventListener('cart:build', (e) => this.handleExternalCartUpdate(e));
    }
  };

  // =============================================================================
  // CTA Updater Module
  // =============================================================================
  const CTAUpdater = {
    updateMealBoxButtons: function() {
      // Get current cart count
      CartUtils.fetchCart()
        .then(cart => {
          const hasItems = cart.item_count > 0;

          // Update all meal box CTA buttons
          const buttons = document.querySelectorAll('[data-meal-box-cta]');
          buttons.forEach(button => {
            const buildText = button.dataset.buildText;
            const reviewText = button.dataset.reviewText;
            const buildUrl = button.dataset.buildUrl;
            const reviewUrl = button.dataset.reviewUrl;

            if (hasItems) {
              // Update to "Review your cart"
              button.href = reviewUrl || (((window.Shopify && Shopify.routes && Shopify.routes.root) || '/') + 'cart');

              // Update text content
              const textElement = button.querySelector('[data-meal-box-text]');
              if (textElement) {
                textElement.textContent = 'Review Your Cart';
              } else if (reviewText) {
                button.innerHTML = reviewText;
              } else {
                button.textContent = 'Review Your Cart';
              }
            } else {
              // Update to "Get Started"
              button.href = buildUrl || (((window.Shopify && Shopify.routes && Shopify.routes.root) || '/') + 'collections/pet-meals');

              // Update text content
              const textElement = button.querySelector('[data-meal-box-text]');
              if (textElement) {
                textElement.textContent = 'Get Started';
              } else if (buildText) {
                button.innerHTML = buildText;
              } else {
                button.textContent = 'Get Started';
              }
            }
          });
        })
        .catch(error => console.error('Error updating meal box CTAs:', error));
    },

    init: function() {
      // Update on page load
      this.updateMealBoxButtons();

      // Update when cart changes (listen for theme's cart event)
      document.addEventListener('ajaxProduct:added', () => this.updateMealBoxButtons());

      // Also listen for custom cart events
      document.addEventListener('cart:updated', () => this.updateMealBoxButtons());

      // Update when drawer opens (cart might have been updated)
      document.addEventListener('drawerOpen', () => this.updateMealBoxButtons());

      // Update when drawer closes (cart might have been emptied)
      document.addEventListener('drawerClose', () => this.updateMealBoxButtons());
    }
  };

  // =============================================================================
  // Preset Builder Module
  // =============================================================================
  const PresetBuilder = {
    // Build a preset meal box from recipe data
    buildPresetMealBox: function(recipe) {
      if (!recipe || !recipe.items || recipe.items.length === 0) {
        console.error('[Preset Meal Box] Invalid recipe data');
        return;
      }

      // Show confirmation if cart has items
      CartUtils.fetchCart()
        .then(async (cart) => {
          if (cart.items.length > 0) {
            // Cart has items - ask for confirmation
            const message = `Building this meal box will replace your current cart contents.\n\nContinue?`;
            const confirmed = await Toast.confirm(message);
            if (!confirmed) {
              return; // User cancelled
            }
          }

          // Clear cart and add preset items
          this.clearAndBuildCart(recipe.items);
        })
        .catch(error => {
          console.error('[Preset Meal Box] Error checking cart:', error);
        });
    },

    // Clear cart then add preset items
    clearAndBuildCart: function(items) {
      // Show loading state
      const loadingMessage = document.createElement('div');
      loadingMessage.id = 'preset-loading';
      loadingMessage.innerHTML = '<div style="position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%); background: white; padding: 30px; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.15); z-index: 9999; text-align: center;"><div style="font-size: 18px; font-weight: 500; margin-bottom: 10px;">Building your meal box...</div><div style="font-size: 14px; color: #666;">Please wait</div></div>';
      document.body.appendChild(loadingMessage);

      // Clear cart first
      fetch('/cart/clear.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      })
      .then(() => {
        // Add all preset items
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            items: items
          })
        });
      })
      .then(res => res.json())
      .then(() => {
        // Remove loading message
        document.body.removeChild(loadingMessage);

        // Redirect to cart page
        window.location.href = ((window.Shopify && Shopify.routes && Shopify.routes.root) || '/') + 'cart';
      })
      .catch(error => {
        console.error('[Preset Meal Box] Error building meal box:', error);

        // Remove loading message
        if (document.getElementById('preset-loading')) {
          document.body.removeChild(loadingMessage);
        }

        Toast.error('There was an error building your meal box. Please try again.');
      });
    },

    // Parse recipe from product description or metafield
    parseRecipeFromElement: function(element) {
      const recipeData = element.dataset.recipe;
      if (!recipeData) {
        console.error('[Preset Meal Box] No recipe data found');
        return null;
      }

      try {
        return JSON.parse(recipeData);
      } catch (e) {
        console.error('[Preset Meal Box] Error parsing recipe:', e);
        return null;
      }
    },

    // Initialize buttons
    initPresetButtons: function() {
      document.querySelectorAll('.build-preset-meal-box').forEach(button => {
        button.addEventListener('click', (e) => {
          e.preventDefault();

          const recipe = this.parseRecipeFromElement(button);
          if (recipe) {
            this.buildPresetMealBox(recipe);
          }
        });
      });
    },

    init: function() {
      // Initialize on page load
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.initPresetButtons());
      } else {
        this.initPresetButtons();
      }

      // Re-initialize when product content changes (for dynamic loading)
      document.addEventListener('product:loaded', () => this.initPresetButtons());
    }
  };

  // =============================================================================
  // Cart Drawer Actions Module
  // Global functions for drawer onclick handlers (consolidated from cart-drawer-enhanced.js)
  // =============================================================================
  const CartDrawerActions = {
    // Close cart drawer
    close: function() {
      document.dispatchEvent(new CustomEvent('cart:close'));
    },

    // Clear BYO items only (items without _petProfileId)
    clear: function() {
      Toast.confirm('Remove all Build Your Own items?', {
        confirmText: 'Remove',
        cancelText: 'Cancel',
        type: 'warning'
      }).then(function(confirmed) {
        if (!confirmed) {
          return;
        }

        // Fetch cart to identify BYO items
        CartUtils.fetchCart().then(function(cart) {
          // Find BYO items (no _petProfileId property)
          var byoItems = cart.items.filter(function(item) {
            var hasPetProfileId = item.properties && item.properties._petProfileId;
            return !hasPetProfileId;
          });

          if (byoItems.length === 0) {
            return;
          }

          // Build updates object to set BYO items to 0
          var updates = {};
          byoItems.forEach(function(item) {
            updates[item.key] = 0;
          });

          // Remove BYO items
          return fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ updates: updates })
          });
        }).then(function() {
          // Dispatch cart update event
          document.dispatchEvent(new CustomEvent('cart:build', {
            bubbles: true
          }));

          // Close drawer if cart becomes empty
          CartUtils.fetchCart().then(function(cart) {
            if (cart.item_count === 0) {
              setTimeout(function() {
                document.dispatchEvent(new CustomEvent('cart:close'));
              }, 300);
            }
          });
        }).catch(function(error) {
          console.error('[CartDrawerActions] Error clearing BYO items:', error);
          Toast.error('There was an error clearing your items. Please try again.');
        });
      });
    },

    // Auto-fill cart to minimum items
    autoFill: function() {
      fetch('/cart.js')
        .then(res => res.json())
        .then(cart => {
          // Calculate current item count
          let currentItems = 0;
          cart.items.forEach(item => {
            currentItems += item.quantity;
          });

          // Check if already meets minimum
          if (currentItems >= CartUtils.MIN_ITEMS) {
            return;
          }

          // Calculate deficit
          const itemsToAdd = CartUtils.MIN_ITEMS - currentItems;

          if (cart.items.length === 0) {
            Toast.warning('No items found in cart');
            return;
          }

          // Show confirmation
          return Toast.confirm(`We will top up your choices to add ${itemsToAdd} more and reach ${CartUtils.MIN_ITEMS} meal packages. Continue?`, {
            confirmText: 'Fill',
            cancelText: 'Cancel',
            confirmType: 'warning'
          }).then(function(confirmed) {
            if (!confirmed) {
              return;
            }

            // Distribute evenly (round robin)
            const updates = {};
            let unitsDistributed = 0;
            let itemIndex = 0;

            while (unitsDistributed < itemsToAdd) {
              const item = cart.items[itemIndex % cart.items.length];
              const currentQty = updates[item.id] || item.quantity;
              updates[item.id] = currentQty + 1;
              unitsDistributed++;
              itemIndex++;
            }

            // Update cart
            return fetch('/cart/update.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ updates })
            })
            .then(function() {
              // Track autofill event
              if (window.AnalyticsTracker) {
                window.AnalyticsTracker.push({
                  event: 'cart_autofill',
                  items_added: itemsToAdd
                });
              }

              // Wait for cart update to be processed
              setTimeout(function() {
                document.dispatchEvent(new CustomEvent('cart:updated', {
                  bubbles: true
                }));
                document.dispatchEvent(new CustomEvent('cart:build', {
                  bubbles: true
                }));
              }, 150);
            });
          });
        })
        .catch(function(error) {
          console.error('[CartDrawerActions] Error auto-filling cart:', error);
          Toast.error('There was an error auto-filling your meal box. Please try again.');
        });
    },

    // Initialize empty cart auto-close listener and BYO banner handlers
    init: function() {
      var self = this;

      // Close drawer when cart becomes empty
      document.addEventListener('cart:updated', function(e) {
        if (e.detail && e.detail.cart && e.detail.cart.item_count === 0) {
          const drawer = document.getElementById('CartDrawer');
          if (drawer && drawer.classList.contains('drawer--is-open')) {
            document.dispatchEvent(new CustomEvent('cart:close'));
          }
        }
      });

      // BYO banner autofill button handler (delegated for dynamically loaded content)
      document.addEventListener('click', function(e) {
        if (e.target.closest('[data-autofill-byo]')) {
          e.preventDefault();
          self.autoFill();
        }
      });

      // BYO banner clear button handler (delegated for dynamically loaded content)
      document.addEventListener('click', function(e) {
        if (e.target.closest('[data-clear-byo]')) {
          e.preventDefault();
          self.clear();
        }
      });
    }
  };

  // =============================================================================
  // Empty Cart Handler Module
  // Shows toast when clicking cart icon with empty cart
  // =============================================================================
  const EmptyCartHandler = {
    init: function() {
      // Use capture phase to intercept before theme drawer handler
      document.addEventListener('click', (e) => {
        const cartLink = e.target.closest('.js-drawer-open-cart');
        if (!cartLink) return;

        // Prevent default to stop drawer from opening
        e.preventDefault();
        e.stopPropagation();

        // Check if cart is empty
        CartUtils.fetchCart()
          .then(cart => {
            if (cart.item_count === 0) {
              // Cart is empty - show toast
              if (window.Toast) {
                const container = document.querySelector('.toast-container');
                if (container) {
                  container.classList.add('toast-container--empty-cart');
                }

                const toast = Toast.error('Your cart is currently empty.', {
                  dismissible: true,
                  duration: 4000
                });

                // Watch for toast removal and clean up
                const observer = new MutationObserver((mutations) => {
                  mutations.forEach((mutation) => {
                    mutation.removedNodes.forEach((node) => {
                      if (node === toast) {
                        document.dispatchEvent(new CustomEvent('cart:close'));
                        if (container) {
                          container.classList.remove('toast-container--empty-cart');
                        }
                        observer.disconnect();
                      }
                    });
                  });
                });

                if (container) {
                  observer.observe(container, { childList: true });
                }
              }
            } else {
              // Cart has items - open drawer
              document.dispatchEvent(new CustomEvent('cart:open'));
            }
          })
          .catch(error => {
            console.error('[EmptyCartHandler] Error checking cart:', error);
            // On error, open drawer anyway
            document.dispatchEvent(new CustomEvent('cart:open'));
          });
      }, true);
    }
  };

  // =============================================================================
  // BYO Subtotal Module
  // Updates the Build-your-own section subtotal in real-time
  // =============================================================================
  const ByoSubtotal = {
    // Format cents to currency string
    formatMoney: function(cents) {
      return '$' + (cents / 100).toFixed(2);
    },

    // Calculate subtotal for BYO items (no _petProfileId)
    calculateByoSubtotal: function(cart) {
      var subtotal = 0;
      cart.items.forEach(function(item) {
        var hasPetProfileId = item.properties && item.properties._petProfileId;
        if (!hasPetProfileId) {
          subtotal += item.final_line_price;
        }
      });
      return subtotal;
    },

    // Update all BYO subtotal elements
    updateDisplay: function(cart) {
      var subtotal = this.calculateByoSubtotal(cart);
      var formatted = this.formatMoney(subtotal);
      var elements = document.querySelectorAll('.cart__byo-subtotal');
      elements.forEach(function(el) {
        el.textContent = formatted;
      });
    },

    init: function() {
      var self = this;
      // Listen for cart updates with cart data
      document.addEventListener('cart:updated', function(e) {
        if (e.detail && e.detail.cart) {
          self.updateDisplay(e.detail.cart);
        }
      });
      // Also listen for cart:built (after DOM rebuild)
      document.addEventListener('cart:built', function() {
        // Fetch fresh cart data to update subtotal
        CartUtils.fetchCart().then(function(cart) {
          self.updateDisplay(cart);
        });
      });
    }
  };

  // =============================================================================
  // Subscription Terms Modal Module
  // Shows subscription disclosure modal before checkout for subscription carts
  // =============================================================================
  const SubscriptionTermsModal = {
    GRAMS_PER_LB: 453.592,
    _resolveCheckout: null, // Promise resolver for checkout continuation

    // Check if cart has subscription items
    hasSubscriptionItems: function(cart) {
      if (!cart || !cart.items) return false;
      return cart.items.some(function(item) {
        return item.selling_plan_allocation != null;
      });
    },

    // Get subscription frequency label from cart
    getFrequencyLabel: function(cart) {
      if (!cart || !cart.items) return 'Unknown';
      for (var i = 0; i < cart.items.length; i++) {
        var item = cart.items[i];
        if (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) {
          return item.selling_plan_allocation.selling_plan.name || 'Subscription';
        }
      }
      return 'Subscription';
    },

    // Calculate total cart weight in lbs
    getCartWeightLbs: function(cart) {
      if (!cart || !cart.items) return 0;
      var totalGrams = 0;
      cart.items.forEach(function(item) {
        if (item.grams) {
          totalGrams += item.grams * item.quantity;
        }
      });
      return Math.round(totalGrams / this.GRAMS_PER_LB);
    },

    // Populate modal with cart data
    populateModal: function(cart) {
      var weightEl = document.querySelector('[data-modal-box-weight]');
      var frequencyEl = document.querySelector('[data-modal-frequency]');
      var firstChargeEl = document.querySelector('[data-modal-first-charge]');
      var nextChargeEl = document.querySelector('[data-modal-next-charge]');

      if (weightEl) {
        weightEl.textContent = this.getCartWeightLbs(cart) + ' lbs of fresh meals';
      }
      if (frequencyEl) {
        frequencyEl.textContent = this.getFrequencyLabel(cart);
      }
      if (firstChargeEl) {
        firstChargeEl.textContent = 'Today (' + CartUtils.formatMoney(cart.total_price) + ')';
      }
      if (nextChargeEl) {
        nextChargeEl.textContent = '~' + this.getFrequencyLabel(cart).toLowerCase() + ' from delivery';
      }
    },

    // Show the modal
    show: function(cart) {
      var self = this;
      var modal = document.getElementById('subscriptionTermsModal');
      if (!modal) {
        console.warn('[SubscriptionTermsModal] Modal element not found');
        return Promise.resolve(true); // Proceed without modal
      }

      this.populateModal(cart);
      modal.style.display = 'flex';
      document.body.style.overflow = 'hidden'; // Prevent background scroll

      // Focus the continue button for accessibility
      var continueBtn = modal.querySelector('[data-modal-continue]');
      if (continueBtn) {
        setTimeout(function() { continueBtn.focus(); }, 100);
      }

      // Return a promise that resolves when user clicks continue, rejects on cancel
      return new Promise(function(resolve, reject) {
        self._resolveCheckout = { resolve: resolve, reject: reject };
      });
    },

    // Hide the modal
    hide: function() {
      var modal = document.getElementById('subscriptionTermsModal');
      if (modal) {
        modal.style.display = 'none';
        document.body.style.overflow = '';
      }
    },

    // Open Gorgias chat
    openChat: function() {
      if (typeof GorgiasChat !== 'undefined' && GorgiasChat.open) {
        GorgiasChat.open();
      } else if (typeof window.GorgiasChat !== 'undefined' && window.GorgiasChat.open) {
        window.GorgiasChat.open();
      } else {
        // Fallback: navigate to contact page
        window.open('/pages/contact', '_blank');
      }
    },

    // Initialize event listeners
    init: function() {
      var self = this;
      var modal = document.getElementById('subscriptionTermsModal');
      if (!modal) return;

      // Cancel button
      var cancelBtn = modal.querySelector('[data-modal-cancel]');
      if (cancelBtn) {
        cancelBtn.addEventListener('click', function() {
          self.hide();
          if (self._resolveCheckout) {
            self._resolveCheckout.reject('cancelled');
            self._resolveCheckout = null;
          }
        });
      }

      // Continue button
      var continueBtn = modal.querySelector('[data-modal-continue]');
      if (continueBtn) {
        continueBtn.addEventListener('click', function() {
          self.hide();
          if (self._resolveCheckout) {
            self._resolveCheckout.resolve(true);
            self._resolveCheckout = null;
          }
        });
      }

      // Backdrop click closes modal (cancel)
      var backdrop = modal.querySelector('.subscription-modal__backdrop');
      if (backdrop) {
        backdrop.addEventListener('click', function() {
          self.hide();
          if (self._resolveCheckout) {
            self._resolveCheckout.reject('cancelled');
            self._resolveCheckout = null;
          }
        });
      }

      // Chat button opens Gorgias
      var chatBtn = modal.querySelector('[data-open-chat]');
      if (chatBtn) {
        chatBtn.addEventListener('click', function() {
          self.openChat();
        });
      }

      // ESC key closes modal
      document.addEventListener('keydown', function(e) {
        if (e.key === 'Escape' && modal.style.display !== 'none') {
          self.hide();
          if (self._resolveCheckout) {
            self._resolveCheckout.reject('cancelled');
            self._resolveCheckout = null;
          }
        }
      });
    }
  };

  // =============================================================================
  // Feeding Guide Generator Module
  // Generates PDF feeding guide via mailer API when checking out with meal plan
  // =============================================================================
  const FeedingGuideGenerator = {
    API_URL: 'https://us-central1-tomsawyer-feeding-guide-mailer.cloudfunctions.net/send-feeding-guide',
    BATCH_API_URL: 'https://us-central1-tomsawyer-feeding-guide-mailer.cloudfunctions.net/sendFeedingGuideBatch',
    DOG_PKG_SIZE: 454, // grams
    CAT_PKG_SIZE: 227, // grams

    // Get API key from theme settings
    getApiKey: function() {
      return window.theme?.settings?.feedingGuideApiKey || null;
    },

    // Get customer email from localStorage (MPQ customer data or calculator settings)
    getCustomerEmailFromStorage: function() {
      try {
        // First check MPQ customer data
        const mpqCustomer = localStorage.getItem('volt-mp-ls-customer');
        if (mpqCustomer) {
          const customer = JSON.parse(mpqCustomer);
          if (customer.email) {
            return customer.email;
          }
        }

        // Fall back to calculator settings
        const calcSettings = localStorage.getItem('ts-calculator-settings');
        if (calcSettings) {
          const calc = JSON.parse(calcSettings);
          if (calc.email) {
            return calc.email;
          }
        }

        return null;
      } catch (e) {
        console.error('[FeedingGuideGenerator] Error reading customer email from storage:', e);
        return null;
      }
    },

    // Check if cart contains a meal plan bundle (Awtomic dynamicBaB)
    hasMealPlanBundle: function(cart) {
      return cart.items.some(item => {
        // Check for _bundle_type (set by meal-plan-loader.js)
        // or _bundleType (set by cart-manager.js)
        const bundleType = item.properties?._bundle_type || item.properties?._bundleType;
        return bundleType === 'dynamicBaB';
      });
    },

    // Extract unique pet profile IDs from cart items with meal plan bundles
    getUniquePetProfileIds: function(cart) {
      const petIds = new Set();
      cart.items.forEach(item => {
        const bundleType = item.properties?._bundle_type || item.properties?._bundleType;
        const petProfileId = item.properties?._petProfileId;
        if (bundleType === 'dynamicBaB' && petProfileId) {
          petIds.add(petProfileId);
        }
      });
      return Array.from(petIds);
    },

    // Get pet profile from localStorage (current pet - legacy)
    getPetProfile: function() {
      try {
        const profiles = JSON.parse(localStorage.getItem('ts-pet-profiles') || '[]');
        const currentId = localStorage.getItem('ts-current-pet-profile-id');
        if (currentId) {
          return profiles.find(p => p.id === currentId) || profiles[0];
        }
        return profiles[0] || null;
      } catch (e) {
        console.error('[FeedingGuideGenerator] Error reading pet profile:', e);
        return null;
      }
    },

    // Get pet profile by ID from localStorage
    getPetProfileById: function(profileId) {
      try {
        const profiles = JSON.parse(localStorage.getItem('ts-pet-profiles') || '[]');
        return profiles.find(p => p.id === profileId) || null;
      } catch (e) {
        console.error('[FeedingGuideGenerator] Error reading pet profile by ID:', e);
        return null;
      }
    },

    // Get meal plan from localStorage
    getMealPlan: function(profileId) {
      try {
        const mealPlans = JSON.parse(localStorage.getItem('ts-meal-plans') || '[]');
        return mealPlans.find(mp => mp.pet_profile_id === profileId) || null;
      } catch (e) {
        console.error('[FeedingGuideGenerator] Error reading meal plan:', e);
        return null;
      }
    },

    // Calculate feeding amounts for meals in the bundle
    calculateMeals: function(mealPlan, caloriesPerDay, petType) {
      const meals = {};
      const pkgSize = petType === 'cat' ? this.CAT_PKG_SIZE : this.DOG_PKG_SIZE;
      const mealProductData = window.mealProductData || {};

      if (!mealPlan || !mealPlan.bundle) return meals;

      mealPlan.bundle.forEach(item => {
        const productData = mealProductData[item.handle];
        if (!productData || !productData.kcal_per_100g) return;

        const kcalPer100g = productData.kcal_per_100g;
        const gramsPerDay = Math.round((caloriesPerDay / kcalPer100g) * 100);
        const packagesPerDay = Math.round((gramsPerDay / pkgSize) * 10) / 10;

        meals[productData.title] = {
          gramsPerDay: gramsPerDay,
          packagesPerDay: parseFloat(packagesPerDay.toFixed(1))
        };
      });

      return meals;
    },

    // Check if string is a valid UUID format
    isValidUUID: function(str) {
      if (!str || typeof str !== 'string') return false;
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      return uuidRegex.test(str);
    },

    // Generate UUIDv7 for feeding guide ID
    generateUUID: function() {
      const timestamp = Date.now();
      const randomBytes = new Uint8Array(10);
      crypto.getRandomValues(randomBytes);
      const bytes = new Uint8Array(16);
      bytes[0] = (timestamp / 0x10000000000) & 0xff;
      bytes[1] = (timestamp / 0x100000000) & 0xff;
      bytes[2] = (timestamp / 0x1000000) & 0xff;
      bytes[3] = (timestamp / 0x10000) & 0xff;
      bytes[4] = (timestamp / 0x100) & 0xff;
      bytes[5] = timestamp & 0xff;
      bytes[6] = 0x70 | (randomBytes[0] & 0x0f);
      bytes[7] = randomBytes[1];
      bytes[8] = 0x80 | (randomBytes[2] & 0x3f);
      bytes[9] = randomBytes[3];
      bytes[10] = randomBytes[4];
      bytes[11] = randomBytes[5];
      bytes[12] = randomBytes[6];
      bytes[13] = randomBytes[7];
      bytes[14] = randomBytes[8];
      bytes[15] = randomBytes[9];
      const hex = Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
      return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    },

    // Build payload for mailer API
    buildPayload: function(profile, mealPlan) {
      if (!profile) return null;

      const petType = profile.type || 'dog';
      const caloriesPerDay = parseFloat(profile.calories_per_day) || 0;
      const meals = this.calculateMeals(mealPlan, caloriesPerDay, petType);

      // Don't send if no meals calculated
      if (Object.keys(meals).length === 0) {
        console.warn('[FeedingGuideGenerator] No meals to include in feeding guide');
        return null;
      }

      // Use profile ID if valid UUID, otherwise generate a new one
      // (Legacy profiles may have non-UUID IDs like "7642570628_001")
      const feedingGuideId = this.isValidUUID(profile.id) ? profile.id : this.generateUUID();
      if (feedingGuideId !== profile.id) {
        console.log('[FeedingGuideGenerator] Generated new UUID for legacy profile:', profile.id, '->', feedingGuideId);
      }

      return {
        id: feedingGuideId,
        petName: profile.name || 'Your Pet',
        petType: petType,
        weight: parseFloat(profile.weight_lbs) || 0,
        weightUnit: 'pound',
        dateOfBirth: profile.date_of_birth || null,
        caloriesPerDay: caloriesPerDay,
        portion: parseFloat(profile.portion_size) || 1,
        servingsPerDay: 1,
        meals: meals,
        source: 'meal-plan-questionnaire',
        market: (theme && theme.settings && theme.settings.market) || 'CA'
      };
    },

    // Call mailer API to generate PDF
    generateFeedingGuide: async function() {
      const profile = this.getPetProfile();
      console.log('[FeedingGuideGenerator] Pet profile:', profile);
      if (!profile) {
        console.warn('[FeedingGuideGenerator] No pet profile found');
        return null;
      }

      const mealPlan = this.getMealPlan(profile.id);
      console.log('[FeedingGuideGenerator] Meal plan:', mealPlan);
      const payload = this.buildPayload(profile, mealPlan);
      console.log('[FeedingGuideGenerator] Payload:', payload);

      if (!payload) {
        return null;
      }

      console.log('[FeedingGuideGenerator] Generating feeding guide for:', payload.petName);

      const apiKey = this.getApiKey();
      if (!apiKey) {
        console.error('[FeedingGuideGenerator] No API key configured');
        return null;
      }

      try {
        const response = await fetch(this.API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-API-Key': apiKey
          },
          body: JSON.stringify(payload)
        });

        if (!response.ok) {
          throw new Error(`API returned ${response.status}`);
        }

        const data = await response.json();
        console.log('[FeedingGuideGenerator] PDF generated:', data.pdfUrl);
        return data.pdfUrl || null;
      } catch (error) {
        console.error('[FeedingGuideGenerator] Error generating feeding guide:', error);
        return null;
      }
    },

    // Generate feeding guides for multiple pets via batch API
    generateBatchFeedingGuides: async function(petProfileIds) {
      if (!petProfileIds || petProfileIds.length === 0) {
        console.warn('[FeedingGuideGenerator] No pet profile IDs provided for batch generation');
        return [];
      }

      // Build payloads for all pets
      const pets = [];
      console.log('[FeedingGuideGenerator] Building payloads for profile IDs:', petProfileIds);
      for (const profileId of petProfileIds) {
        const profile = this.getPetProfileById(profileId);
        if (!profile) {
          console.warn('[FeedingGuideGenerator] Profile not found for ID:', profileId);
          continue;
        }
        const mealPlan = this.getMealPlan(profile.id);
        console.log('[FeedingGuideGenerator] Profile:', profile.name, '| Meal plan:', mealPlan ? 'found' : 'NOT FOUND');
        const payload = this.buildPayload(profile, mealPlan);
        if (payload) {
          console.log('[FeedingGuideGenerator] Payload built for:', payload.petName, payload.id);
          pets.push(payload);
        } else {
          console.warn('[FeedingGuideGenerator] Payload build failed for:', profile.name);
        }
      }

      if (pets.length === 0) {
        console.warn('[FeedingGuideGenerator] No valid payloads to send');
        return [];
      }

      console.log('[FeedingGuideGenerator] Generating batch feeding guides for', pets.length, 'pets');

      const apiKey = this.getApiKey();
      if (!apiKey) {
        console.error('[FeedingGuideGenerator] No API key configured');
        return [];
      }

      // Get customer email for Klaviyo event
      // Try window.SHOPIFY_CUSTOMER_EMAIL (from Gorgias), window.ShopObjCustomer (from MPQ), or localStorage
      const customerEmail = window.SHOPIFY_CUSTOMER_EMAIL ||
                           window.ShopObjCustomer?.email ||
                           this.getCustomerEmailFromStorage();

      if (customerEmail) {
        console.log('[FeedingGuideGenerator] Including email for Klaviyo event');
      } else {
        console.log('[FeedingGuideGenerator] No customer email available - Klaviyo event will be skipped');
      }

      try {
        const requestBody = { pets: pets };
        if (customerEmail) {
          requestBody.email = customerEmail;
        }

        const response = await fetch(this.BATCH_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-API-Key': apiKey
          },
          body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
          throw new Error(`Batch API returned ${response.status}`);
        }

        const data = await response.json();
        console.log('[FeedingGuideGenerator] Batch response:', data);

        // Return array of URLs (pet_name and pet_id are embedded in filename)
        return (data.results || [])
          .filter(r => r.success)
          .map(r => r.url);
      } catch (error) {
        console.error('[FeedingGuideGenerator] Error in batch generation:', error);
        return [];
      }
    },

    // Add feeding guide URL(s) as cart attribute
    // Supports both single URL (legacy) and array of filenames (multi-pet)
    addCartAttribute: async function(feedingGuides) {
      if (!feedingGuides) return false;

      try {
        const attributes = {};

        // Check if it's a multi-pet array or single URL
        if (Array.isArray(feedingGuides)) {
          // Multi-pet: store as JSON array
          console.log('[FeedingGuideGenerator] Adding cart attribute with', feedingGuides.length, 'feeding guides');
          attributes['Meal Plan Feeding Guides'] = JSON.stringify(feedingGuides);
          // Clear legacy single-URL attribute if exists
          attributes['Meal Plan Feeding Guide'] = '';
        } else {
          // Single pet (legacy): store as URL string
          console.log('[FeedingGuideGenerator] Adding cart attribute with single URL:', feedingGuides);
          attributes['Meal Plan Feeding Guide'] = feedingGuides;
        }

        const response = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: attributes })
        });
        const cart = await response.json();
        console.log('[FeedingGuideGenerator] Cart attribute response:', cart.attributes);

        // Verify the attribute was set
        if (Array.isArray(feedingGuides)) {
          if (cart.attributes && cart.attributes['Meal Plan Feeding Guides']) {
            console.log('[FeedingGuideGenerator] Multi-pet cart attribute verified successfully');
            return true;
          }
        } else {
          if (cart.attributes && cart.attributes['Meal Plan Feeding Guide'] === feedingGuides) {
            console.log('[FeedingGuideGenerator] Single-pet cart attribute verified successfully');
            return true;
          }
        }
        console.error('[FeedingGuideGenerator] Cart attribute NOT set. Current attributes:', cart.attributes);
        return false;
      } catch (error) {
        console.error('[FeedingGuideGenerator] Error adding cart attribute:', error);
        return false;
      }
    },

    // Clear any stale feeding guide cart attributes
    clearFeedingGuideAttributes: async function() {
      try {
        const response = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            attributes: {
              'Meal Plan Feeding Guide': '',
              'Meal Plan Feeding Guides': ''
            }
          })
        });
        const cart = await response.json();
        console.log('[FeedingGuideGenerator] Cleared stale attributes. Current:', cart.attributes);
        return true;
      } catch (error) {
        console.error('[FeedingGuideGenerator] Error clearing attributes:', error);
        return false;
      }
    },

    // Handle checkout form submission
    handleCheckout: async function(evt) {
      const form = evt.target;
      if (!form || form.id !== 'CartPageForm') return;

      console.log('[FeedingGuideGenerator] Checkout form submitted - intercepting');

      // MUST prevent default immediately before any async operations
      evt.preventDefault();

      const submitBtn = form.querySelector('button[type="submit"]');
      const originalText = submitBtn ? submitBtn.textContent : '';

      try {
        // Fetch cart data
        const cart = await CartUtils.fetchCart();
        console.log('[FeedingGuideGenerator] Cart items:', cart.items.map(function(i) {
          return { handle: i.handle, bundleHandle: i.properties?._bundleHandle, petProfileId: i.properties?._petProfileId };
        }));

        // Check if cart has subscription items - show terms modal if so
        const hasSubscription = SubscriptionTermsModal.hasSubscriptionItems(cart);
        const hasMealPlan = this.hasMealPlanBundle(cart);

        console.log('[FeedingGuideGenerator] hasSubscription:', hasSubscription, 'hasMealPlan:', hasMealPlan);

        // Check if subscription confirmation modal is enabled (theme setting, default off)
        const showConfirmationModal = window.__cartSettings?.showSubscriptionConfirmation === true;

        // If subscription cart AND confirmation modal is enabled, show terms modal
        if (hasSubscription && showConfirmationModal) {
          console.log('[SubscriptionTermsModal] Showing subscription terms modal');

          // Start PDF generation in background (if meal plan) while modal is displayed
          let pdfPromise = null;
          if (hasMealPlan) {
            const petProfileIds = this.getUniquePetProfileIds(cart);
            console.log('[FeedingGuideGenerator] Starting background PDF generation for:', petProfileIds);
            if (petProfileIds.length > 0) {
              pdfPromise = this.generateBatchFeedingGuides(petProfileIds);
            }
          }

          // Show modal and wait for user decision
          try {
            await SubscriptionTermsModal.show(cart);
            console.log('[SubscriptionTermsModal] User accepted terms');
          } catch (cancelReason) {
            // User cancelled - do not proceed
            console.log('[SubscriptionTermsModal] User cancelled checkout:', cancelReason);
            return;
          }

          // User clicked "Continue" - wait for PDF if it was generating
          if (pdfPromise) {
            if (submitBtn) {
              submitBtn.disabled = true;
              submitBtn.textContent = 'Preparing order...';
            }

            const feedingGuides = await pdfPromise;
            console.log('[FeedingGuideGenerator] PDF generation complete:', feedingGuides);

            if (feedingGuides && (Array.isArray(feedingGuides) ? feedingGuides.length > 0 : true)) {
              await this.addCartAttribute(feedingGuides);
            } else {
              await this.clearFeedingGuideAttributes();
            }
          }

          // Proceed to checkout
          if (submitBtn) {
            submitBtn.textContent = 'Redirecting to checkout...';
          }
          form.removeEventListener('submit', this._boundHandler);
          form.submit();
          return;
        }

        // Non-subscription cart - check for meal plan and generate PDF if needed
        if (!hasMealPlan) {
          console.log('[FeedingGuideGenerator] No meal plan bundle - proceeding to checkout');
          form.removeEventListener('submit', this._boundHandler);
          form.submit();
          return;
        }

        console.log('[FeedingGuideGenerator] Meal plan bundle detected - generating feeding guide(s)');

        // Show loading state on checkout button
        if (submitBtn) {
          submitBtn.disabled = true;
          submitBtn.textContent = 'Processing...';
        }

        // Extract unique pet profile IDs from cart
        const petProfileIds = this.getUniquePetProfileIds(cart);
        console.log('[FeedingGuideGenerator] Unique pet profile IDs:', petProfileIds);

        let feedingGuides = null;

        if (petProfileIds.length > 0) {
          feedingGuides = await this.generateBatchFeedingGuides(petProfileIds);
          console.log('[FeedingGuideGenerator] Feeding guides:', feedingGuides);
        } else {
          console.log('[FeedingGuideGenerator] No pet profile IDs - skipping feeding guide generation');
        }

        if (feedingGuides && (Array.isArray(feedingGuides) ? feedingGuides.length > 0 : true)) {
          // Add as cart attribute (handles both array and single URL)
          const attributeAdded = await this.addCartAttribute(feedingGuides);
          console.log('[FeedingGuideGenerator] Attribute added result:', attributeAdded);
        } else {
          // Clear any stale feeding guide attributes to prevent wrong PDF from previous order
          console.warn('[FeedingGuideGenerator] No feeding guides returned - clearing stale attributes');
          await this.clearFeedingGuideAttributes();
        }

        // Submit form to proceed to checkout
        if (submitBtn) {
          submitBtn.textContent = 'Redirecting to checkout...';
        }

        console.log('[FeedingGuideGenerator] Submitting form to checkout');
        // Use native form submit to bypass our listener
        form.removeEventListener('submit', this._boundHandler);
        form.submit();
      } catch (error) {
        console.error('[FeedingGuideGenerator] Checkout error:', error);
        // Restore button and allow retry
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.textContent = originalText;
        }
        Toast.error('There was an error preparing your order. Please try again.');
      }
    },

    init: function() {
      // Bind handler for reference (so we can remove it later)
      this._boundHandler = this.handleCheckout.bind(this);

      // Listen for cart form submissions
      document.addEventListener('submit', this._boundHandler, true);
    }
  };

  // =============================================================================
  // USD Discount Applicator - Apply FRESHSTART on checkout for USD customers
  // =============================================================================
  const USDDiscountApplicator = {
    DISCOUNT_CODE: 'FRESHSTART',
    applied: false,

    /**
     * Check if current currency is USD
     */
    isUSD: function() {
      return window.Shopify?.currency?.active === 'USD' ||
             window.theme?.tnsSettings?.currency === 'USD';
    },

    /**
     * Apply discount code via /discount/{code} endpoint
     * This sets a session cookie that persists to checkout
     */
    applyDiscount: function() {
      if (this.applied || !this.isUSD()) {
        return Promise.resolve(false);
      }

      console.log('[USDDiscount] Applying FRESHSTART discount code');

      return fetch('/discount/' + encodeURIComponent(this.DISCOUNT_CODE), {
        method: 'GET',
        credentials: 'same-origin'
      })
        .then(response => {
          if (response.ok) {
            this.applied = true;
            console.log('[USDDiscount] FRESHSTART discount applied successfully');
            return true;
          }
          console.warn('[USDDiscount] Failed to apply discount, status:', response.status);
          return false;
        })
        .catch(err => {
          console.warn('[USDDiscount] Error applying discount:', err);
          return false;
        });
    },

    /**
     * Initialize - intercept checkout to apply discount for USD customers
     * Discount is only applied when customer clicks checkout, not before
     */
    init: function() {
      if (!this.isUSD()) {
        return;
      }

      // Intercept checkout form submission to apply discount
      document.addEventListener('submit', (e) => {
        const form = e.target;
        if (form.action && form.action.includes('/checkout') && !this.applied) {
          e.preventDefault();
          this.applyDiscount().then(() => {
            form.submit();
          });
        }
      }, true);
    }
  };

  // =============================================================================
  // Checkout Minimum Guard
  // Prevents form submission to /checkout when cart is below minimum item count.
  // The quantity inputs inside #CartPageForm can trigger form submission via Enter key,
  // bypassing the hidden checkout button.
  // =============================================================================
  var CheckoutMinGuard = {
    init: function() {
      var form = document.getElementById('CartPageForm');
      if (!form) return;

      form.addEventListener('submit', function(e) {
        // Count ONLY primary meals, mirroring MealTally + boxPacker semantics.
        // Each .js-qty__num input now carries data-handle (cart-item.liquid)
        // so we can resolve product_type via window.mealProductData without
        // an extra fetch. Items not in the meal data lookup (Bundles, Branded
        // Merchandise, complements, etc.) don't count toward the min — same
        // gate MealTally enforces visually.
        var totalItems = 0;
        var hasTrialBox = false;
        var items = document.querySelectorAll('.js-qty__num');
        items.forEach(function(input) {
          if (CartUtils.isTrialBox(input.dataset.handle)) {
            hasTrialBox = true;
          }
          if (CartUtils.isPrimaryMeal(input.dataset.handle)) {
            totalItems += parseInt(input.value, 10) || 0;
          }
        });

        // A Trial Box clears the minimum ONLY when it is the whole order —
        // totalItems here is already the primary-meal count, so `=== 0` means the
        // customer has no meals of their own. With meals present they must still
        // reach the minimum, or a sub-minimum Build-a-Box reaches checkout.
        // Published by MealTally from the full cart (it can see selling plans and
        // _bundleUid, which the DOM cannot). Falls back to the conservative
        // "trial box and no meals of their own" test if the flag is not set yet.
        const published = form.dataset.trialClearsMin;
        const trialClearsMin = published !== undefined
          ? published === 'true'
          : (hasTrialBox && totalItems === 0);
        if (!trialClearsMin && totalItems < CartUtils.MIN_ITEMS) {
          e.preventDefault();
          e.stopImmediatePropagation();
          console.warn('[CheckoutMinGuard] Blocked checkout: ' + totalItems + ' meal items < ' + CartUtils.MIN_ITEMS + ' minimum');
          // Scroll to warning message
          var warning = document.querySelector('.cart__min-meal-warning');
          if (warning) {
            warning.scrollIntoView({ behavior: 'smooth', block: 'center' });
            warning.style.animation = 'none';
            warning.offsetHeight; // trigger reflow
            warning.style.animation = 'flash-warning 0.6s ease 2';
          }
        }
      });
    }
  };

  // =============================================================================
  // Initialize All Modules
  // =============================================================================
  CheckoutMinGuard.init();
  MealTally.init();
  SubscriptionSelector.initEvents();
  ItemRemover.init();
  MealBoxAdder.init();
  GridButtonSync.init();
  CTAUpdater.init();
  PresetBuilder.init();
  CartDrawerActions.init();
  EmptyCartHandler.init();
  ByoSubtotal.init();
  SubscriptionTermsModal.init();
  FeedingGuideGenerator.init();
  USDDiscountApplicator.init();

  // Expose modules for use by other scripts
  window.CartManager = {
    SubscriptionSelector: SubscriptionSelector,
    GridButtonSync: GridButtonSync,
    ItemRemover: ItemRemover,
    FeedingGuideGenerator: FeedingGuideGenerator,
    SubscriptionTermsModal: SubscriptionTermsModal
  };

  // Expose global functions for onclick handlers (backwards compatible with cart-drawer-enhanced.js)
  window.closeCartDrawer = CartDrawerActions.close;
  window.clearCartDrawer = CartDrawerActions.clear;
  window.autoFillCartDrawer = CartDrawerActions.autoFill;

  // Handle "Add more meals" button - close drawer and navigate (or scroll if already on page)
  window.handleAddMoreMeals = function() {
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    const targetPath = root + 'collections/pet-meals';
    const currentPath = window.location.pathname;

    // Check if already on pet-meals collection
    if (currentPath === targetPath || currentPath === targetPath + '/') {
      // Already on page - just close drawer and scroll to #menu
      CartDrawerActions.close();
      const menuEl = document.getElementById('menu');
      if (menuEl) {
        setTimeout(function() {
          menuEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 300); // Wait for drawer close animation
      }
    } else {
      // Different page - close and redirect
      CartDrawerActions.close();
      window.location.href = targetPath + '#menu';
    }
  };

  window.CartUtils = CartUtils;

  // =============================================================================
  // Global: Load pet profile and navigate to MPQ
  // Used by cart grouping headers and item links to edit specific pet's plan
  // =============================================================================
  window.loadPetAndNavigate = function(profileId, destination) {
    destination = destination || 'meals';
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

    if (!profileId) {
      console.warn('[loadPetAndNavigate] No profile ID provided');
      window.location.href = root + 'pages/meal-plan-questionnaire';
      return;
    }

    // Set the profile ID as the current pet (raw string, no JSON.stringify)
    localStorage.setItem('ts-current-pet-profile-id', profileId);

    // Set edit mode flag so MPQ knows we came from cart
    // This enables "Back to cart" vs "Update cart" behavior
    if (destination === 'meals') {
      sessionStorage.setItem('mpq-edit-from-cart', profileId);
    }

    // Map destination to MPQ path URL (MPQ parses pathname, not query params)
    var pathMap = {
      'profile': '/pet_profile#2',
      'meals': '/meals'
    };

    var pathSuffix = pathMap[destination] || '/meals';
    var targetUrl = root + 'pages/meal-plan-questionnaire' + pathSuffix;

    console.log('[loadPetAndNavigate] Loading pet:', profileId, 'destination:', destination);

    // Close cart drawer if open
    if (typeof CartDrawerActions !== 'undefined' && CartDrawerActions.close) {
      CartDrawerActions.close();
    }

    window.location.href = targetUrl;
  };

  // =============================================================================
  // Global: Remove all meal plan items for a specific pet from cart
  // Used by cart grouping headers trash icon
  // =============================================================================
  window.removePetMealPlan = async function(profileId, petName) {
    if (!profileId) {
      console.warn('[removePetMealPlan] No profile ID provided');
      return;
    }

    var displayName = petName || 'this pet';

    // Show confirmation toast
    var confirmed = await Toast.confirm(
      'Remove ' + displayName + '\'s entire meal plan from your cart?',
      {
        confirmText: 'Remove',
        cancelText: 'Cancel',
        type: 'warning'
      }
    );

    if (!confirmed) return;

    try {
      // Fetch cart to find items with matching _petProfileId
      var cart = await CartUtils.fetchCart();
      var updates = {};
      var itemsToRemove = 0;

      cart.items.forEach(function(item) {
        var itemPetId = null;
        if (item.properties) {
          for (var key in item.properties) {
            if (key === '_petProfileId' && item.properties[key] === profileId) {
              itemPetId = profileId;
              break;
            }
          }
        }
        if (itemPetId === profileId) {
          // Use item key to set quantity to 0
          updates[item.key] = 0;
          itemsToRemove++;
        }
      });

      if (itemsToRemove === 0) {
        console.warn('[removePetMealPlan] No items found for profile:', profileId);
        Toast.show('No items found for ' + displayName, { type: 'info' });
        return;
      }

      console.log('[removePetMealPlan] Removing', itemsToRemove, 'items for pet:', profileId);

      // Use /cart/update.js to remove all items at once
      var updatedCart = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: updates })
      }).then(function(res) { return res.json(); });

      // Check if cart is now empty
      if (updatedCart.item_count === 0) {
        // Dispatch cart:updated for button sync
        document.dispatchEvent(new CustomEvent('cart:updated', {
          bubbles: true,
          detail: { cart: updatedCart }
        }));

        // On /cart page, reload to show empty state; otherwise close drawer
        if (window.location.pathname === ((window.Shopify && Shopify.routes && Shopify.routes.root) || '/') + 'cart') {
          Toast.show(displayName + '\'s meal plan removed from cart', { type: 'success' });
          setTimeout(function() { window.location.reload(); }, 500);
          return;
        } else {
          document.dispatchEvent(new CustomEvent('cart:close'));
        }
      } else {
        // Trigger cart rebuild for remaining items
        document.dispatchEvent(new CustomEvent('cart:build'));
      }

      Toast.show(displayName + '\'s meal plan removed from cart', { type: 'success' });

    } catch (error) {
      console.error('[removePetMealPlan] Error:', error);
      Toast.show('Failed to remove meal plan. Please try again.', { type: 'error' });
    }
  };

})();
